import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Workbook } from '@fortune-sheet/react';
import '@fortune-sheet/react/dist/index.css';
import { getUTData, saveUTData, regenerateSubcardImages } from '../utils/storage';
import UTLayoutConfigModal, { getLayoutConfig } from './UTLayoutConfigModal';
import UTFunctionsModal from './UTFunctionsModal';
import SubcardImageWarningModal from './SubcardImageWarningModal';
import './UTTablePage.css';

// FortuneSheet's default font index 0 is "Times New Roman"; index 1 is
// "Arial". We force every cell to ff=1 so the spreadsheet renders in
// Arial (matches the rest of the app).
const ARIAL_FF = 1;

const BLANK_SHEET = [
  {
    name: 'UT Measurements',
    celldata: [],
    row: 50,
    column: 26,
  },
];

// ── Formula reference rewriter on cell-move ───────────────────────────
// FortuneSheet 1.0.4 does NOT update cross-references when cells are
// moved (cut + paste / drag without Ctrl). That means: if cell A1
// contains 10 and cell C5 contains "=A1+1", moving A1 to E9 leaves
// C5's formula as "=A1+1" — pointing at the now-empty A1, returning
// an error. Excel's behaviour is that the formula in C5 auto-updates
// to "=E9+1" to track A1's new address. We replicate that ourselves.
//
// The moved cells' OWN formulas are intentionally left alone — also
// matching Excel: moving a formula cell preserves its references
// because the formula represents a calculation on specific source data.
//
// Convert a "row_col" key string to a {r, c} object and back.
function _addrToRC(addr) {
  const [r, c] = addr.split('_').map(Number);
  return { r, c };
}
function _rcToAddr(r, c) {
  return `${r}_${c}`;
}

// Convert 0-based column index ↔ letter sequence (0=A, 25=Z, 26=AA…).
function _colIdxToLetters(idx) {
  let s = '';
  let n = idx;
  while (n >= 0) {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  }
  return s;
}
function _colLettersToIdx(letters) {
  let n = 0;
  for (let i = 0; i < letters.length; i++) {
    n = n * 26 + (letters.charCodeAt(i) - 64);
  }
  return n - 1;
}

// Cheap structural-equality check for two cell objects. Compares the
// JSON serialisations — anything FortuneSheet stores in the cell
// (v, f, m, ct, bl, etc.) is captured.
function _cellFingerprint(cell) {
  try { return JSON.stringify(cell); } catch { return null; }
}

// Rewrite a formula string, replacing any cell reference whose address
// matches a key in `moveMap` with the address it moved to. Preserves
// "$" absolute markers — Excel keeps absolute markers across cross-
// reference updates because the user's intent (lock-this-row /
// lock-this-col) is independent of where the referenced cell now lives.
//
// Range refs (A1:B3): only shifted when BOTH endpoints moved by the
// same row+col offset (Excel behaviour for moved blocks). Partial
// overlaps leave the range intact.
// Single-ref regex — uses lookbehind / lookahead to skip refs that are
// part of a range (preceded or followed by ":"). Ranges are handled
// holistically in the range pass below.
const _REF_RE = /(?<!:)(\$?)([A-Z]{1,3})(\$?)(\d{1,7})(?!:)/g;

function _addrFromRef(colLetters, rowDigits) {
  return _rcToAddr(parseInt(rowDigits, 10) - 1, _colLettersToIdx(colLetters));
}

function _refFromAddr(addr, dollarCol, dollarRow) {
  const { r, c } = _addrToRC(addr);
  return `${dollarCol}${_colIdxToLetters(c)}${dollarRow}${r + 1}`;
}

function _rewriteFormulaRefs(formula, moveMap) {
  if (typeof formula !== 'string' || !formula) return formula;

  // First pass: ranges. Look for `A1:B3`-style tokens and update only
  // when both endpoints have the same shift.
  const rangeRe = /(\$?)([A-Z]{1,3})(\$?)(\d{1,7}):(\$?)([A-Z]{1,3})(\$?)(\d{1,7})/g;
  let result = formula.replace(rangeRe, (full, d1, c1, d2, r1, d3, c2, d4, r2) => {
    const a1 = _addrFromRef(c1, r1);
    const a2 = _addrFromRef(c2, r2);
    const m1 = moveMap[a1];
    const m2 = moveMap[a2];
    if (!m1 && !m2) return full;        // neither moved
    if (!m1 || !m2) return full;        // only one moved → leave range
    const { r: r1n, c: c1n } = _addrToRC(m1);
    const { r: r1o, c: c1o } = _addrToRC(a1);
    const { r: r2n, c: c2n } = _addrToRC(m2);
    const { r: r2o, c: c2o } = _addrToRC(a2);
    if (r1n - r1o !== r2n - r2o || c1n - c1o !== c2n - c2o) return full;
    return `${_refFromAddr(m1, d1, d2)}:${_refFromAddr(m2, d3, d4)}`;
  });

  // Second pass: single refs.
  result = result.replace(_REF_RE, (full, dc, col, dr, row) => {
    const addr = _addrFromRef(col, row);
    const next = moveMap[addr];
    if (!next) return full;
    return _refFromAddr(next, dc, dr);
  });

  return result;
}

// FortuneSheet's onChange persists each sheet with a 2D `data` matrix
// (cell objects or null per row/col). On remount, the Workbook only reads
// `celldata` (sparse list) to populate cells. So when reopening a saved
// table we have to walk `data` and emit a celldata entry for every cell
// that has a value. We also drop the inflated runtime-only fields so the
// remount payload is small and predictable.
function normalizeSheet(s) {
  if (!s || typeof s !== 'object') return s;

  let celldata = Array.isArray(s.celldata) ? [...s.celldata] : [];
  const seen = new Set(celldata.map((c) => `${c.r}:${c.c}`));

  if (Array.isArray(s.data)) {
    s.data.forEach((row, r) => {
      if (!Array.isArray(row)) return;
      row.forEach((cell, c) => {
        if (!cell) return;
        // Skip empty/blank cells.
        if (typeof cell === 'object') {
          const hasV = cell.v !== undefined && cell.v !== null && cell.v !== '';
          const hasM = cell.m !== undefined && cell.m !== null && cell.m !== '';
          if (!hasV && !hasM) return;
        } else if (cell === '') {
          return;
        }
        const key = `${r}:${c}`;
        if (seen.has(key)) return;
        seen.add(key);
        const v = typeof cell === 'object' ? cell : { v: cell, m: String(cell) };
        celldata.push({ r, c, v });
      });
    });
  }

  // Compute row/column counts that comfortably contain the data.
  let maxR = 0;
  let maxC = 0;
  for (const cd of celldata) {
    if (cd.r > maxR) maxR = cd.r;
    if (cd.c > maxC) maxC = cd.c;
  }

  // Preserve merge ranges so reopened sheets show the same merged cells.
  // FortuneSheet keeps these on `config.merge` keyed "r_c" -> {r,c,rs,cs}.
  const mergeCfg = s.config?.merge && typeof s.config.merge === 'object'
    ? s.config.merge
    : null;

  // Expand row/col counts to also cover merge ranges.
  if (mergeCfg) {
    for (const k of Object.keys(mergeCfg)) {
      const m = mergeCfg[k];
      if (!m) continue;
      const lastR = (m.r ?? 0) + (m.rs ?? 1) - 1;
      const lastC = (m.c ?? 0) + (m.cs ?? 1) - 1;
      if (lastR > maxR) maxR = lastR;
      if (lastC > maxC) maxC = lastC;
    }
  }

  // Spread the original sheet so FortuneSheet-internal config (column/row
  // metadata, calc chain, borders, etc.) survives the round-trip. Without
  // these, reopened sheets render but feel "frozen" — cell edits don't
  // propagate because internal state wasn't fully bootstrapped. We then
  // override celldata (canonical input) and drop the 2D `data` matrix so
  // celldata is the single source of truth on remount.
  const out = {
    ...s,
    name: s.name || 'UT Measurements',
    celldata,
    row: Math.max(50, maxR + 5, s.row || 0),
    column: Math.max(26, maxC + 5, s.column || 0),
  };
  delete out.data;
  return out;
}

// Normalize loaded sheets so every existing cell has ff=Arial.
function applyArialFont(sheets) {
  if (!Array.isArray(sheets)) return sheets;
  return sheets.map((raw) => {
    const s = normalizeSheet(raw);
    return {
      ...s,
      celldata: (s.celldata || []).map((cd) => {
        if (!cd || !cd.v || typeof cd.v !== 'object') return cd;
        if (cd.v.ff !== undefined && cd.v.ff !== null && cd.v.ff !== 0 && cd.v.ff !== '0') return cd;
        return { ...cd, v: { ...cd.v, ff: ARIAL_FF } };
      }),
    };
  });
}

// Whitelist of FortuneSheet toolbar items the user may use. Anything not
// listed is hidden from the toolbar (font-color / conditional-format /
// sort / filter / link / image / comment / "more" / freeze / autosum
// stay hidden). `background` (cell fill) is exposed so users can
// highlight cells — the colour is preserved into the Word PDF and the
// web preview via cell.bg passthrough (see v3.1.3 commit).
const ALLOWED_TOOLBAR_ITEMS = [
  'undo',
  'redo',
  'format-painter',
  'clear-format',
  '|',
  'currency-format',
  'percentage-format',
  'number-decrease',
  'number-increase',
  'format',
  '|',
  'font',
  'font-size',
  '|',
  'bold',
  'italic',
  'strike-through',
  'underline',
  '|',
  'background',
  'border',
  'merge-cell',
  '|',
  'horizontal-align',
  'vertical-align',
  'text-wrap',
  'text-rotation',
  '|',
  'find',
];

// Right-click menu inside cells: trim down to plain operations.
const ALLOWED_CELL_CONTEXT = [
  'copy', 'paste', 'clear', '|',
  'row', 'column', '|',
  'order', 'frozen',
];

// Sheet-tab right-click is fully empty: no rename / move / hide.
const EMPTY_SHEET_TAB_CONTEXT = [];

export default function UTTablePage() {
  const { inspectionId, tableId } = useParams();
  const [sheets, setSheets] = useState(null);
  const [tableHeading, setTableHeading] = useState('UT Measurements');
  const [component, setComponent] = useState('');
  const [measurement, setMeasurement] = useState('');
  const [scanlineCount, setScanlineCount] = useState(null);
  const [positionCount, setPositionCount] = useState(null);
  const [allTables, setAllTables] = useState([]);
  const [showLayoutCfg, setShowLayoutCfg] = useState(false);
  const [showFunctions, setShowFunctions] = useState(false);
  // Warning modal state for auto-image regen failures (file missing, halt, …).
  const [imageWarning, setImageWarning] = useState(null);
  // Bumped each time we apply a generated template. Used as the key on
  // the Workbook so it remounts and reads the fresh `data` prop.
  // (FortuneSheet ignores `data` after the initial mount, so without
  // this nothing visible changes when we swap the sheets array.)
  const [workbookEpoch, setWorkbookEpoch] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const workbookRef = useRef();
  const containerRef = useRef();
  // Cell-update batcher for the move-detector. afterUpdateCell fires
  // per-cell during a drag-move (source-cleared, destination-filled,
  // sometimes interleaved). We accumulate them here, then debounce-fire
  // a single move-detection pass once the user's action settles.
  const detectTimerRef = useRef(null);
  // Guard: when WE update formulas programmatically via setCellValue,
  // afterUpdateCell + onChange fire again — skip those re-entrant
  // updates so we don't loop.
  const isApplyingFixRef = useRef(false);
  // Latest sheets, mirrored as a ref so the detector reads the freshest
  // data when its debounce fires.
  const latestSheetsRef = useRef([]);
  // "Stable" baseline — updated AFTER each detection pass, so a multi-
  // event burst diffs against the pre-burst snapshot rather than the
  // intermediate states.
  const baselineSheetsRef = useRef([]);

  useEffect(() => {
    const initSheets = (s) => {
      const arialed = applyArialFont(s);
      baselineSheetsRef.current = arialed;
      latestSheetsRef.current = arialed;
      setSheets(arialed);
      // Same trick as applyTemplate — for sheets loaded from the
      // backend, FortuneSheet stores cell.f text but doesn't register
      // the cell in its formula dependency graph until each formula
      // is seen by setCellValue. Without this, formula cells in
      // existing tables show stale (or empty) values and don't
      // recompute when the user types into referenced cells. Slightly
      // longer delay because the initial Workbook mount can take
      // longer than a template-apply remount.
      registerFormulasInSheets(arialed, 500);
    };
    getUTData(inspectionId)
      .then((res) => {
        const data = res?.ut_data;
        if (data && Array.isArray(data.tables)) {
          setAllTables(data.tables);
          if (tableId) {
            const match = data.tables.find((t) => t.id === tableId);
            if (match) {
              setTableHeading(match.heading || 'UT Measurements');
              setComponent(match.component || '');
              setMeasurement(match.measurement || '');
              setScanlineCount(match.scanlineCount ?? null);
              setPositionCount(match.positionCount ?? null);
              // Empty array is truthy, so `match.sheets || BLANK_SHEET`
              // falls through and FortuneSheet crashes on no-sheet data
              // — e.g. the CW-OSI seeded sub-card before Configure
              // Layout has been applied. Treat empty/non-array the same
              // as missing.
              const hasSheets = Array.isArray(match.sheets) && match.sheets.length > 0;
              initSheets(hasSheets ? match.sheets : BLANK_SHEET);
              return;
            }
          }
          initSheets(BLANK_SHEET);
          return;
        }
        initSheets(BLANK_SHEET);
      })
      .catch(() => initSheets(BLANK_SHEET));
  }, [inspectionId, tableId]);

  useEffect(() => {
    document.title = `${tableHeading} — TIB`;
  }, [tableHeading]);

  // ── Strip-formatting paste ───────────────────────────────────────
  // Capture paste events inside the workbook container and rewrite the
  // clipboard so only `text/plain` reaches FortuneSheet — that drops any
  // styles, fills, fonts, hyperlinks, images, and HTML quirks. The user
  // gets a clean values-only paste every time, regardless of source.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const handlePaste = (e) => {
      const cd = e.clipboardData;
      if (!cd) return;
      const hasHtml = Array.from(cd.types || []).includes('text/html');
      // Only intervene if there's HTML-formatted content; pure-text pastes
      // already go through cleanly without our help.
      if (!hasHtml) return;
      // FortuneSheet's own copy embeds the marker "fortune-copy-action-table"
      // in the HTML payload — that's how it round-trips formulas, formats,
      // and merges between cells. If we see that marker, let the original
      // paste through untouched so internal copy/paste keeps working.
      const html = cd.getData('text/html') || '';
      if (html.indexOf('fortune-copy-action-table') > -1) return;
      const text = cd.getData('text/plain') || '';
      // Build a synthetic event with only text/plain set so FortuneSheet's
      // own paste handler (which runs on the bubbling phase) sees clean text.
      const dt = new DataTransfer();
      dt.setData('text/plain', text);
      const synth = new ClipboardEvent('paste', {
        clipboardData: dt,
        bubbles: true,
        cancelable: true,
      });
      // Prevent the original event entirely, then re-dispatch the clean one.
      e.stopPropagation();
      e.preventDefault();
      e.target.dispatchEvent(synth);
    };
    root.addEventListener('paste', handlePaste, true);
    return () => root.removeEventListener('paste', handlePaste, true);
  }, [sheets]);

  // ── Drag the formula reference highlight ──────────────────────────
  // FortuneSheet 1.0.4 renders coloured borders around every cell
  // referenced in the formula currently being edited, but does NOT
  // make those borders draggable. We implement that ourselves:
  //
  //  1. Listen for mousedown on the highlight border edges
  //     (`.fortune-formula-functionrange-highlight .fortune-copy`).
  //  2. Determine which reference is being grabbed by matching the
  //     highlight's DOM index against the `rangeindex` spans inside
  //     the formula editor.
  //  3. On mouseup, compute the target cell from the drop coordinates
  //     (using the workbookRef's getColumnWidth / getRowHeight APIs
  //     to walk the per-column / per-row pixel grid).
  //  4. Rewrite the matching `<span rangeindex="N">` in the formula
  //     editor's contentEditable, fire an input event so FortuneSheet
  //     re-parses, and re-focus the editor so the user can continue.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;

    // Active drag state (lives outside React state — we just need
    // refs while the gesture is in flight).
    let drag = null;

    function findCellInputEditor() {
      // The FortuneSheet contentEditable that holds the live formula.
      return (
        document.getElementById('luckysheet-rich-text-editor') ||
        root.querySelector('.luckysheet-cell-input')
      );
    }

    function pixelToCell(x, y) {
      // `x` and `y` are pixels inside the .fortune-cell-area element
      // (i.e. (0,0) is the top-left of the cell grid, NOT the page).
      const wb = workbookRef.current;
      if (!wb) return null;
      const s = Array.isArray(sheets) && sheets[0] ? sheets[0] : null;
      const maxRow = (s?.row) || 200;
      const maxCol = (s?.column) || 26;
      let col = 0;
      let accumX = 0;
      while (col < maxCol) {
        let w;
        try { w = wb.getColumnWidth([col])?.[col]; } catch { w = undefined; }
        if (w == null) w = 73;
        if (accumX + w > x) break;
        accumX += w;
        col += 1;
      }
      let row = 0;
      let accumY = 0;
      while (row < maxRow) {
        let h;
        try { h = wb.getRowHeight([row])?.[row]; } catch { h = undefined; }
        if (h == null) h = 19;
        if (accumY + h > y) break;
        accumY += h;
        row += 1;
      }
      return { row, col };
    }

    function colIdxToLetters(idx) {
      let s = '';
      let n = idx;
      while (n >= 0) {
        s = String.fromCharCode(65 + (n % 26)) + s;
        n = Math.floor(n / 26) - 1;
      }
      return s;
    }

    function rewriteSingleRef(refText, newRow, newCol) {
      // Preserve $ markers from the original.
      const m = (refText || '').match(/^(\$?)([A-Z]+)(\$?)(\d+)$/);
      const dc = m ? m[1] : '';
      const dr = m ? m[3] : '';
      return `${dc}${colIdxToLetters(newCol)}${dr}${newRow + 1}`;
    }

    function rewriteRangeRef(rangeText, newRow, newCol, oldRow, oldCol) {
      // Range looks like A1:B3. Shift both endpoints by the delta
      // between the OLD top-left and the NEW top-left.
      const m = (rangeText || '').match(/^(\$?)([A-Z]+)(\$?)(\d+):(\$?)([A-Z]+)(\$?)(\d+)$/);
      if (!m) return rangeText;
      const d1c = m[1], c1l = m[2], d1r = m[3], r1n = parseInt(m[4], 10);
      const d2c = m[5], c2l = m[6], d2r = m[7], r2n = parseInt(m[8], 10);
      const cFromL = (s) => {
        let n = 0;
        for (let i = 0; i < s.length; i += 1) n = n * 26 + (s.charCodeAt(i) - 64);
        return n - 1;
      };
      const c1 = cFromL(c1l), c2 = cFromL(c2l);
      const r1 = r1n - 1, r2 = r2n - 1;
      const dR = newRow - oldRow;
      const dC = newCol - oldCol;
      return `${d1c}${colIdxToLetters(c1 + dC)}${d1r}${r1 + dR + 1}:${d2c}${colIdxToLetters(c2 + dC)}${d2r}${r2 + dR + 1}`;
    }

    // Helper: dispatch keydown + input so FortuneSheet's editor onChange
    // re-parses the formula and re-renders the coloured highlights.
    // See v3.1.1 commit msg for the keydown-then-input rationale.
    function fireEditorReparse(editor) {
      try {
        const kd = new KeyboardEvent('keydown', {
          key: 'a', code: 'KeyA', keyCode: 65, which: 65,
          bubbles: true, cancelable: true,
        });
        editor.dispatchEvent(kd);
      } catch { /* older browsers may reject the ctor */ }
      try {
        editor.dispatchEvent(new InputEvent('input', { bubbles: true }));
      } catch {
        const ev = document.createEvent('Event');
        ev.initEvent('input', true, true);
        editor.dispatchEvent(ev);
      }
      editor.focus?.();
    }

    // If the user has TEXT-SELECTED a reference inside the formula
    // editor (eg double-clicked the "N21" token), find the smallest
    // reference span that fully contains the selection. Returns the
    // span element or null. Used to implement "click target cell
    // replaces the highlighted reference" — FortuneSheet's default
    // behaviour inserts the new reference next to it instead.
    function findRefSpanContainingSelection() {
      const sel = window.getSelection?.();
      if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null;
      const range = sel.getRangeAt(0);
      const editor = findCellInputEditor();
      if (!editor || !editor.contains(range.commonAncestorContainer)) return null;
      // Walk up from commonAncestorContainer until we hit a ref span.
      let node = range.commonAncestorContainer;
      while (node && node !== editor) {
        if (
          node.nodeType === 1 &&
          node.classList?.contains('fortune-formula-functionrange-cell')
        ) {
          return node;
        }
        node = node.parentNode;
      }
      return null;
    }

    function onMouseDown(e) {
      const cellArea = root.querySelector('.fortune-cell-area');

      // ── Path A: click on a cell while a ref is text-selected ──
      // The user has selected a token like "N21" inside the formula
      // editor and is now clicking another cell to replace it.
      // FortuneSheet's default inserts a NEW span next to the
      // selected one (producing "O30N21"). We intercept the
      // mousedown, replace the selected span's text with the
      // clicked cell's address, and short-circuit the default.
      if (cellArea && cellArea.contains(e.target)) {
        const editorA = findCellInputEditor();
        const selSpan = editorA ? findRefSpanContainingSelection() : null;
        if (selSpan && editorA) {
          // We'll compute the target cell on mouseup (same gesture).
          drag = {
            mode: 'replace-selection',
            refSpan: selSpan,
            editor: editorA,
            cellArea,
          };
          // FortuneSheet's handleCellAreaMouseDown would otherwise
          // see israngeseleciton(ctx)=true (cursor inside a ref
          // span) and insert a new span. Block it.
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }

      // ── Path B: drag the highlight border (existing behaviour) ──
      // Only act on the highlight border edges (top/right/bottom/left).
      const edge = e.target.closest('.fortune-formula-functionrange-highlight .fortune-copy');
      if (!edge) return;
      const highlightEl = edge.closest('.fortune-formula-functionrange-highlight');
      if (!highlightEl) return;

      // The DOM order of highlight divs maps 1:1 to the formula's
      // referenced-range index (rangeindex on the cell spans).
      const allHighlights = Array.from(
        root.querySelectorAll('.fortune-formula-functionrange-highlight'),
      );
      const idx = allHighlights.indexOf(highlightEl);
      if (idx < 0) return;

      const editor = findCellInputEditor();
      if (!editor) return;
      const refSpans = Array.from(
        editor.querySelectorAll('span.fortune-formula-functionrange-cell'),
      );
      if (idx >= refSpans.length) return;
      const refSpan = refSpans[idx];

      if (!cellArea) return;

      // Decode the current ref text so we know whether it's a single
      // ref or a range, and (for ranges) what the original top-left
      // was so we can shift correctly.
      const refText = (refSpan.textContent || '').trim();
      const singleMatch = refText.match(/^\$?([A-Z]+)\$?(\d+)$/);
      const rangeMatch = refText.match(/^\$?([A-Z]+)\$?(\d+):\$?[A-Z]+\$?\d+$/);
      const isRange = !!rangeMatch && !singleMatch;
      let oldRow = 0; let oldCol = 0;
      if (isRange) {
        const lettersToIdx = (s) => {
          let n = 0;
          for (let i = 0; i < s.length; i += 1) n = n * 26 + (s.charCodeAt(i) - 64);
          return n - 1;
        };
        oldCol = lettersToIdx(rangeMatch[1]);
        oldRow = parseInt(rangeMatch[2], 10) - 1;
      }

      drag = {
        mode: 'drag-highlight',
        refSpan, cellArea, editor, isRange, oldRow, oldCol,
      };
      // Stop FortuneSheet from interpreting this mousedown as a
      // formula-range click (which would re-set the reference to
      // the cell beneath the highlight border, i.e. the SAME cell).
      e.preventDefault();
      e.stopPropagation();
    }

    function onMouseUp(e) {
      if (!drag) return;
      const state = drag;
      drag = null;
      const { cellArea, editor, refSpan } = state;
      const rect = cellArea.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      // Drop landed outside the cell grid — cancel.
      if (x < 0 || y < 0 || x > rect.width || y > rect.height) return;
      const cell = pixelToCell(x, y);
      if (!cell) return;

      let newText;
      if (state.mode === 'replace-selection') {
        // Always write a single-cell address (the user clicked one
        // cell — if they want a range they can adjust afterwards).
        newText = `${colIdxToLetters(cell.col)}${cell.row + 1}`;
      } else {
        newText = state.isRange
          ? rewriteRangeRef(refSpan.textContent, cell.row, cell.col, state.oldRow, state.oldCol)
          : rewriteSingleRef(refSpan.textContent, cell.row, cell.col);
      }
      if (!newText || newText === refSpan.textContent) return;

      refSpan.textContent = newText;

      // Force FortuneSheet to re-parse + re-render highlights.
      fireEditorReparse(editor);
    }

    root.addEventListener('mousedown', onMouseDown, true);
    document.addEventListener('mouseup', onMouseUp, true);
    return () => {
      root.removeEventListener('mousedown', onMouseDown, true);
      document.removeEventListener('mouseup', onMouseUp, true);
    };
  }, [sheets]);

  // Snapshot-based move detector. Wraps the Workbook's onChange. The
  // detector runs on a short debounce so multi-step internal state
  // changes (e.g. a drag-move that ultimately fires a single onChange,
  // but the entire ctx update happens synchronously inside
  // onCellsMoveEnd) settle into a single comparison.
  function buildCellMap(sheet) {
    const m = new Map();
    const data = sheet && Array.isArray(sheet.data) ? sheet.data : null;
    if (!data) return m;
    for (let r = 0; r < data.length; r++) {
      const row = data[r];
      if (!Array.isArray(row)) continue;
      for (let c = 0; c < row.length; c++) {
        const cell = row[c];
        if (cell && typeof cell === 'object') {
          if (cell.v == null && cell.m == null && cell.f == null) continue;
          m.set(_rcToAddr(r, c), cell);
        }
      }
    }
    return m;
  }

  function runMoveDetection() {
    if (isApplyingFixRef.current) return;
    const baseline = baselineSheetsRef.current;
    const cur = latestSheetsRef.current;
    if (!Array.isArray(baseline) || !Array.isArray(cur)) {
      baselineSheetsRef.current = cur;
      return;
    }

    let totalMoves = 0;
    for (let si = 0; si < cur.length; si++) {
      const baseSheet = baseline[si];
      const curSheet = cur[si];
      if (!baseSheet || !curSheet) continue;
      const prevMap = buildCellMap(baseSheet);
      const nextMap = buildCellMap(curSheet);

      const removed = [];
      const added = [];
      for (const [addr, cell] of prevMap) {
        const ncell = nextMap.get(addr);
        if (ncell && _cellFingerprint(ncell) === _cellFingerprint(cell)) continue;
        removed.push({ addr, fp: _cellFingerprint(cell) });
      }
      for (const [addr, cell] of nextMap) {
        const pcell = prevMap.get(addr);
        if (pcell && _cellFingerprint(pcell) === _cellFingerprint(cell)) continue;
        added.push({ addr, fp: _cellFingerprint(cell) });
      }
      if (!removed.length || !added.length) continue;

      const usedAdded = new Set();
      const moveMap = {};
      for (const { addr, fp } of removed) {
        if (!fp) continue;
        const i = added.findIndex((a, idx) => !usedAdded.has(idx) && a.fp === fp);
        if (i >= 0) {
          usedAdded.add(i);
          moveMap[addr] = added[i].addr;
        }
      }
      const initialCount = Object.keys(moveMap).length;
      if (!initialCount) continue;

      // ── Block-move inference ────────────────────────────────────
      // FortuneSheet auto-shifts RANGE refs inside a moved block but
      // does NOT shift single-cell refs. That changes the fingerprint
      // of formula cells with range refs at the destination — so our
      // fingerprint matcher misses them.
      //
      // Compute the dominant (rOff, cOff) — the offset shared by the
      // largest number of matched entries. Build the bounding box
      // from sources that match that dominant offset, then back-fill
      // every position in that rectangle with the dominant offset
      // (regardless of whether the source had content — formulas
      // that reference an empty cell inside the bounding box would
      // also need to be redirected to the corresponding new empty
      // cell, which gives the same outcome).
      const offsets = Object.entries(moveMap).map(([from, to]) => {
        const a = _addrToRC(from), b = _addrToRC(to);
        return { from, to, rOff: b.r - a.r, cOff: b.c - a.c };
      });
      const offsetCounts = new Map();
      for (const o of offsets) {
        const k = `${o.rOff}_${o.cOff}`;
        offsetCounts.set(k, (offsetCounts.get(k) || 0) + 1);
      }
      let dominantKey = null;
      let dominantCount = 0;
      for (const [k, n] of offsetCounts) {
        if (n > dominantCount) { dominantCount = n; dominantKey = k; }
      }
      const dominant = (() => {
        const [rs, cs] = dominantKey.split('_').map(Number);
        return { rOff: rs, cOff: cs };
      })();
      // Only run block inference if the dominant offset accounts for
      // at least 2 entries (avoid mis-shifting on isolated edits).
      let inferred = 0;
      if (dominantCount >= 2) {
        let minR = Infinity, maxR = -Infinity, minC = Infinity, maxC = -Infinity;
        for (const o of offsets) {
          if (o.rOff !== dominant.rOff || o.cOff !== dominant.cOff) continue;
          const { r, c } = _addrToRC(o.from);
          if (r < minR) minR = r;
          if (r > maxR) maxR = r;
          if (c < minC) minC = c;
          if (c > maxC) maxC = c;
        }
        for (let r = minR; r <= maxR; r++) {
          for (let c = minC; c <= maxC; c++) {
            const src = _rcToAddr(r, c);
            if (moveMap[src]) continue;
            moveMap[src] = _rcToAddr(r + dominant.rOff, c + dominant.cOff);
            inferred += 1;
          }
        }
      }
      const moveCount = Object.keys(moveMap).length;
      // eslint-disable-next-line no-console
      console.info(`[UT move-rewriter] sheet ${si}: matched ${initialCount}, dominant offset (${dominant.rOff},${dominant.cOff}) ×${dominantCount}, inferred ${inferred}, total ${moveCount}`);

      const wb = workbookRef.current;
      if (!wb || typeof wb.setCellValue !== 'function') {
        // eslint-disable-next-line no-console
        console.warn('[UT move-rewriter] workbook ref unavailable; skipping rewrite');
        continue;
      }
      const data = Array.isArray(curSheet.data) ? curSheet.data : null;
      if (!data) continue;
      isApplyingFixRef.current = true;
      let rewriteCount = 0;
      try {
        // Rewrite refs in EVERY formula cell — including the moved
        // ones. The rewrite is a no-op for any ref that isn't in
        // moveMap, so a moved formula cell whose refs point OUTSIDE
        // the moved block stays unchanged (Excel-parity "move
        // preserves refs to unmoved cells"), while refs pointing
        // INSIDE the moved block follow the move.
        for (let r = 0; r < data.length; r++) {
          const row = data[r];
          if (!Array.isArray(row)) continue;
          for (let c = 0; c < row.length; c++) {
            const cell = row[c];
            if (!cell || typeof cell !== 'object' || !cell.f) continue;
            const newF = _rewriteFormulaRefs(cell.f, moveMap);
            if (newF === cell.f) continue;
            try {
              // CRITICAL: pass ONLY { f: newF }. Do NOT spread the
              // existing cell or include v/m — the inner
              // setCellValue$1 runs a _.forEach(value, …) AFTER
              // updateCell has computed the new v + m, and any keys
              // we include in `value` get copy-pasted over the
              // freshly-computed values. With just { f: newF }:
              //  - setCellValue$1 hits its formula branch
              //    (value.f != null && value.v == null).
              //  - updateCell calls execfunction(newF), computes v
              //    and m, writes them onto data[r][c].
              //  - The trailing forEach only iterates over { f: newF },
              //    so it just re-asserts cell.f = newF (no-op for v/m).
              //  - The cell's other props (bl, ff, ct, etc.) are
              //    untouched because they were never in `value`.
              wb.setCellValue(r, c, { f: newF });
              rewriteCount += 1;
            } catch (e) {
              // eslint-disable-next-line no-console
              console.warn('[UT move-rewriter] setCellValue failed at', r, c, e);
            }
          }
        }
      } finally {
        setTimeout(() => { isApplyingFixRef.current = false; }, 80);
      }
      totalMoves += moveCount;
      // eslint-disable-next-line no-console
      console.info(`[UT move-rewriter] sheet ${si}: ${moveCount} move(s), rewrote ${rewriteCount} formula(s)`);
    }
    if (totalMoves === 0) {
      // eslint-disable-next-line no-console
      console.debug('[UT move-rewriter] onChange fired, no moves detected');
    }
    // Always refresh baseline after a detection pass.
    baselineSheetsRef.current = latestSheetsRef.current;
  }

  function handleSheetsChange(next) {
    setSheets(next);
    latestSheetsRef.current = next;
    if (isApplyingFixRef.current) {
      // Our own programmatic setCellValue fired onChange — don't
      // detect on our own writes.
      baselineSheetsRef.current = next;
      return;
    }
    if (detectTimerRef.current) clearTimeout(detectTimerRef.current);
    detectTimerRef.current = setTimeout(runMoveDetection, 150);
  }

  // Strip any non-JSON-serializable bits FortuneSheet might leave on the data
  // (functions, undefined, circular hooks). The fastest way is a JSON
  // round-trip — anything that can't survive it shouldn't be persisted anyway.
  function sanitizeSheets(data) {
    try {
      return JSON.parse(JSON.stringify(data));
    } catch {
      return data;
    }
  }

  const handleSave = async () => {
    if (!tableId) {
      setSaveMsg('Missing table ID');
      return;
    }
    setSaving(true);
    setSaveMsg('');
    try {
      // FortuneSheet's ref doesn't expose getData() — the canonical source
      // of truth is the `sheets` state, which we keep in sync via the
      // Workbook's onChange handler.
      const current = sanitizeSheets(sheets);
      const exists = allTables.some((t) => t.id === tableId);
      let nextTables;
      if (exists) {
        nextTables = allTables.map((t) =>
          t.id === tableId ? { ...t, sheets: current } : t,
        );
      } else {
        nextTables = [
          ...allTables,
          { id: tableId, heading: tableHeading, sheets: current, collapsed: false },
        ];
      }
      await saveUTData(inspectionId, { tables: nextTables });
      setAllTables(nextTables);
      setSaveMsg('Saved');

      // Notify the originating tab so it can refresh and show the new table
      // contents in the sub-card. window.opener is the exact tab that opened
      // this one, so multiple TIB tabs aren't an issue.
      try {
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage({
            type: 'tib:ut-saved',
            inspectionId,
            tableId,
          }, window.location.origin);
          window.opener.focus();
        }
      } catch (postErr) {
        // Cross-origin or already-closed opener — fall through to closing.
        console.warn('Could not notify opener tab:', postErr);
      }

      // Close the tab after a short beat so the user sees "Saved".
      setTimeout(() => {
        window.close();
      }, 400);
    } catch (err) {
      console.error('UT save failed:', err);
      setSaveMsg(`Save failed: ${err?.message || 'unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  // ── Build nozzle template (specialType: 'nozzle-locations') ───────
  // Layout reworked v3.1.10:
  //  - Col A = Location, Col B = Nozzle (swapped vs old behaviour).
  //  - When the user picks multiple locations (Neck + Plate, etc.)
  //    we split the table by location using the `--` row separator
  //    so each location renders as its own sub-table in the web
  //    preview + Word PDF.
  //  - Within each split: the first row carries the location label
  //    in col A; subsequent rows carry `*` (vertical merge sentinel)
  //    so the rendered preview merges them upward into the label.
  //  - Nominal stays as a COLUMN (each nozzle row has its own
  //    nominal entry); no summary rows.
  //  - Column order: Location | Nozzle | [Nominal] | POS…
  function buildNozzleTemplate({ nozzleCount, locations, includeNominal, colPrefix, colCount }) {
    const HEADER = (val) => ({ v: val, m: String(val), ff: ARIAL_FF, bl: 1, ct: { fa: 'General', t: 'g' } });
    const STAR = () => ({ v: '*', m: '*', ff: ARIAL_FF });
    const SEPARATOR = () => ({ v: '--', m: '--', ff: ARIAL_FF });
    const celldata = [];
    const nomCol  = includeNominal ? 2 : -1;
    const slStart = includeNominal ? 3 : 2;

    // Row 0 — headers
    celldata.push({ r: 0, c: 0, v: HEADER('Location') });
    celldata.push({ r: 0, c: 1, v: HEADER('Nozzle') });
    if (includeNominal) celldata.push({ r: 0, c: nomCol, v: HEADER('Nominal value (mm)') });
    for (let i = 0; i < colCount; i++) {
      celldata.push({ r: 0, c: slStart + i, v: HEADER(`${colPrefix}${i + 1}`) });
    }

    let rowCursor = 1;
    locations.forEach((loc, li) => {
      // Insert a `--` separator row before every block except the
      // first. Splitter (`_split_grid_segments` + UTTablePreview
      // mirror) renders the segments before/after as separate sub-
      // tables in the web preview + Word PDF.
      if (li > 0) {
        celldata.push({ r: rowCursor, c: 0, v: SEPARATOR() });
        rowCursor += 1;
      }
      for (let ni = 0; ni < nozzleCount; ni++) {
        // Col A: location label on the first row, `*` on the rest
        // so the preview merges them into a single vertical block.
        celldata.push({
          r: rowCursor, c: 0,
          v: ni === 0 ? HEADER(loc) : STAR(),
        });
        // Col B: nozzle label N1..Nn
        celldata.push({ r: rowCursor, c: 1, v: HEADER(`N${ni + 1}`) });
        // Nominal + measurement cells left blank for user entry.
        rowCursor += 1;
      }
    });

    return [{
      name: 'UT Measurements',
      celldata,
      row: Math.max(50, rowCursor + 5),
      column: Math.max(26, slStart + colCount + 2),
    }];
  }

  // ── Build rafter template (specialType: 'rafter-locations') ───────
  // Layout reworked v3.1.13:
  //  - Position is col A. Each position is its OWN split sub-table
  //    separated by `--` rows. Col A carries the position label on
  //    the first row + `*` (vertical-merge) on every subsequent row
  //    of the block (data rows + summary rows).
  //  - Col B = "Location" header; cells below run SL1..SLn (scanline
  //    labels — what were COLUMNS in the original layout). Summary
  //    rows put their labels here too.
  //  - Col C = Nominal (when checked). User types the value once on
  //    the first data row of each position block; subsequent data
  //    rows carry `*` (vertical merge); summary rows carry `-*`
  //    (horizontal merge LEFT) so the summary label in col B extends
  //    across into col C.
  //  - Cols D+ (or C+ when no nominal) = location headers
  //    (i), (ii), …, Center (n) — these are what were ROWS in the
  //    original layout.
  //  - Summary rows under each position's data: Lowest / Highest /
  //    Average thk with MIN/MAX/AVERAGE formulas per location
  //    column; if nominal, an Average degradation row with
  //    =IFERROR((C<pipeStart> - <col><avgRow>) / C<pipeStart>, "-")
  //    percent-formatted (every location column references the same
  //    nominal cell).
  function buildRafterTemplate({ locationLabels, positions, includeNominal, colPrefix, colCount }) {
    const HEADER = (val, opts = {}) => ({ v: val, m: String(val), ff: ARIAL_FF, bl: 1, ct: { fa: 'General', t: 'g' }, ...opts });
    const STAR_HDR = () => ({ v: '*', m: '*', ff: ARIAL_FF, bl: 1, ct: { fa: 'General', t: 'g' } });
    const STAR = () => ({ v: '*', m: '*', ff: ARIAL_FF });
    const H_LEFT = () => ({ v: '-*', m: '-*', ff: ARIAL_FF });
    const SEPARATOR = () => ({ v: '--', m: '--', ff: ARIAL_FF });
    const OVERALL_LABEL = () => ({ v: 'Overall', m: 'Overall', ff: ARIAL_FF, bl: 1, borderless: true, ct: { fa: 'General', t: 'g' } });
    const OVERALL_SPACER = () => ({ v: '', m: '', ff: ARIAL_FF, borderless: true, whiteBg: true });
    const ARROW_CELL = () => ({ v: '⇩', m: '⇩', ff: ARIAL_FF, ht: 0, headerBg: true, noLeftBorder: true, noRightBorder: true });
    const STATISTICS_LABEL = () => ({ v: ' ', m: ' ', ff: ARIAL_FF, bl: 1, ht: 1, headerBg: true, noLeftBorder: true, noRightBorder: true, ct: { fa: 'General', t: 'g' } });
    const PERCENT = (formula) => ({
      f: formula, m: '', ff: ARIAL_FF,
      ct: { fa: '0.0%', t: 'n' },
    });
    const FORMULA = (formula) => ({
      f: formula, m: '', ff: ARIAL_FF,
      ct: { fa: 'General', t: 'n' },
    });

    const M = locationLabels.length;
    const N = colCount;
    const celldata = [];
    const locCol = 1;
    const nomCol = includeNominal ? 2 : -1;
    const dataColStart = includeNominal ? 3 : 2;
    const overallCol = dataColStart + M;

    const colLetter = (idx) => {
      let s = '';
      let n = idx;
      while (n >= 0) {
        s = String.fromCharCode(65 + (n % 26)) + s;
        n = Math.floor(n / 26) - 1;
      }
      return s;
    };

    // Row 0 — headers. Overall col is a "spacer" (3-side nil).
    celldata.push({ r: 0, c: 0, v: HEADER('Position') });
    celldata.push({ r: 0, c: locCol, v: HEADER('Location') });
    if (includeNominal) celldata.push({ r: 0, c: nomCol, v: HEADER('Nominal value (mm)') });
    locationLabels.forEach((loc, i) => {
      celldata.push({ r: 0, c: dataColStart + i, v: HEADER(loc) });
    });
    celldata.push({ r: 0, c: overallCol, v: OVERALL_SPACER() });

    const firstDataLet = colLetter(dataColStart);
    const lastDataLet  = colLetter(dataColStart + M - 1);

    let cursor = 1;
    positions.forEach((pos, pi) => {
      // `--` separator row between positions.
      if (pi > 0) {
        celldata.push({ r: cursor, c: 0, v: SEPARATOR() });
        cursor += 1;
      }

      // Data rows for this position — SL1..SLn down col B.
      const dataStartRow = cursor;
      for (let si = 0; si < N; si++) {
        celldata.push({
          r: cursor, c: 0,
          v: si === 0 ? HEADER(pos) : STAR_HDR(),
        });
        celldata.push({ r: cursor, c: locCol, v: HEADER(`${colPrefix}${si + 1}`) });
        if (includeNominal && si > 0) {
          celldata.push({ r: cursor, c: nomCol, v: STAR() });
        }
        celldata.push({ r: cursor, c: overallCol, v: OVERALL_SPACER() });
        cursor += 1;
      }
      const dataEndRow = cursor - 1;
      const dataStartDisp = dataStartRow + 1;
      const dataEndDisp = dataEndRow + 1;

      // Dedicated label row between data and summary. Cells chosen by
      // looking at the first summary row immediately below:
      //   col 0 = * (continues position vertical-merge),
      //   col 1 = "Statistics" header (below is "Lowest thk"),
      //   col 2 = -* (mirrors summary nominal sentinel) when nominal,
      //   cols 3..lastData = ⇩ arrow,
      //   Overall col = bold "Overall" header.
      celldata.push({ r: cursor, c: 0, v: STAR_HDR() });
      celldata.push({ r: cursor, c: locCol, v: STATISTICS_LABEL() });
      if (includeNominal) celldata.push({ r: cursor, c: nomCol, v: H_LEFT() });
      for (let i = 0; i < M; i += 1) {
        celldata.push({ r: cursor, c: dataColStart + i, v: ARROW_CELL() });
      }
      celldata.push({ r: cursor, c: overallCol, v: OVERALL_LABEL() });
      cursor += 1;

      // Summary-row emitter — `-*` on nominal col so col B's label
      // visually merges across into col C; Overall col gets cross-
      // column aggregate. Summary labels in col B carry ht:1.
      function emitSummary(label, perColFormula, overallFormula) {
        celldata.push({ r: cursor, c: 0, v: STAR_HDR() });
        celldata.push({ r: cursor, c: locCol, v: HEADER(label, { ht: 1 }) });
        if (includeNominal) celldata.push({ r: cursor, c: nomCol, v: H_LEFT() });
        for (let i = 0; i < M; i++) {
          const col = colLetter(dataColStart + i);
          celldata.push({
            r: cursor, c: dataColStart + i,
            v: perColFormula(col),
          });
        }
        celldata.push({ r: cursor, c: overallCol, v: overallFormula() });
        cursor += 1;
      }

      emitSummary(
        'Lowest thk',
        (col) => FORMULA(`=MIN(${col}${dataStartDisp}:${col}${dataEndDisp})`),
        () => FORMULA(`=MIN(${firstDataLet}${cursor + 1}:${lastDataLet}${cursor + 1})`),
      );
      emitSummary(
        'Highest thk',
        (col) => FORMULA(`=MAX(${col}${dataStartDisp}:${col}${dataEndDisp})`),
        () => FORMULA(`=MAX(${firstDataLet}${cursor + 1}:${lastDataLet}${cursor + 1})`),
      );
      const avgRow = cursor;
      emitSummary(
        'Average thk',
        (col) => FORMULA(`=AVERAGE(${col}${dataStartDisp}:${col}${dataEndDisp})`),
        () => FORMULA(`=AVERAGE(${firstDataLet}${cursor + 1}:${lastDataLet}${cursor + 1})`),
      );

      if (includeNominal) {
        const avgDisp = avgRow + 1;
        const nomLetter = colLetter(nomCol);
        const nomRowDisp = dataStartRow + 1;
        celldata.push({ r: cursor, c: 0, v: STAR_HDR() });
        celldata.push({ r: cursor, c: locCol, v: HEADER('Average degradation', { ht: 1 }) });
        celldata.push({ r: cursor, c: nomCol, v: H_LEFT() });
        for (let i = 0; i < M; i++) {
          const col = colLetter(dataColStart + i);
          celldata.push({
            r: cursor, c: dataColStart + i,
            v: PERCENT(`=IFERROR((${nomLetter}${nomRowDisp}-${col}${avgDisp})/${nomLetter}${nomRowDisp},"-")`),
          });
        }
        celldata.push({
          r: cursor, c: overallCol,
          v: PERCENT(`=AVERAGE(${firstDataLet}${cursor + 1}:${lastDataLet}${cursor + 1})`),
        });
        cursor += 1;
      }
    });

    return [{
      name: 'UT Measurements',
      celldata,
      row: Math.max(50, cursor + 5),
      column: Math.max(26, overallCol + 2),
    }];
  }

  // ── Build pipe-segment template (specialType: 'pipe-segment') ──
  // Each pipe is its OWN split sub-table separated by `--` rows. A
  // pipe block consists of:
  //  - col 0 ("Pipe"): pipe label on row 1, `*` (vertical merge
  //    sentinel) on every subsequent row of the block. The merge
  //    extends over data rows AND summary rows so the pipe label
  //    visually spans the whole block.
  //  - col 1 ("Location"): location label per data row; summary-row
  //    labels ("Lowest thk", "Highest thk", "Average thk",
  //    "Average degradation") on the summary rows below.
  //  - col 2 ([Nominal], when checked): the user types the nominal
  //    once in the first row of the pipe block; subsequent rows
  //    (including summary rows) carry `*` so the nominal cell
  //    visually merges down the whole block.
  //  - col 3+ (POS): data rows blank for user entry; summary rows
  //    auto-emit per-column MIN/MAX/AVERAGE formulas over the pipe's
  //    data range, plus an "Average degradation" row when nominal is
  //    enabled (=IFERROR((<nomLetter><pipeStart> - <col><avgRow>) /
  //    <nomLetter><pipeStart>, "-") percent-formatted).
  // A `--` separator row sits between consecutive pipes.
  function buildPipeSegmentTemplate({ pipes, includeNominal, colPrefix, colCount, groupHeader = 'Pipe' }) {
    const HEADER = (val, opts = {}) => ({ v: val, m: String(val), ff: ARIAL_FF, bl: 1, ct: { fa: 'General', t: 'g' }, ...opts });
    const STAR_HDR = () => ({ v: '*', m: '*', ff: ARIAL_FF, bl: 1, ct: { fa: 'General', t: 'g' } });
    const STAR = () => ({ v: '*', m: '*', ff: ARIAL_FF });
    const H_LEFT = () => ({ v: '-*', m: '-*', ff: ARIAL_FF });
    const SEPARATOR = () => ({ v: '--', m: '--', ff: ARIAL_FF });
    const OVERALL_LABEL = () => ({ v: 'Overall', m: 'Overall', ff: ARIAL_FF, bl: 1, borderless: true, ct: { fa: 'General', t: 'g' } });
    // Empty cell ABOVE the Overall header — keeps left border (which
    // is the right edge of the data area) but nil on the other three
    // sides so the column reads as a clean band with no horizontal
    // dividers above the Overall label.
    const OVERALL_SPACER = () => ({ v: '', m: '', ff: ARIAL_FF, borderless: true, whiteBg: true });
    // ⇩ U+21E9 DOWNWARDS WHITE ARROW, centered, with no left border
    // so the cell visually flows from the data column on its left.
    const ARROW_CELL = () => ({ v: '⇩', m: '⇩', ff: ARIAL_FF, ht: 0, headerBg: true, noLeftBorder: true, noRightBorder: true });
    // Bold "Statistics" header that sits in the same column as the
    // summary labels (col B for per-split layouts). Left-aligned to
    // match the summary labels below.
    const STATISTICS_LABEL = () => ({ v: ' ', m: ' ', ff: ARIAL_FF, bl: 1, ht: 1, headerBg: true, noLeftBorder: true, noRightBorder: true, ct: { fa: 'General', t: 'g' } });
    const PERCENT = (formula) => ({
      f: formula, m: '', ff: ARIAL_FF,
      ct: { fa: '0.0%', t: 'n' },
    });
    const FORMULA = (formula) => ({
      f: formula, m: '', ff: ARIAL_FF,
      ct: { fa: 'General', t: 'n' },
    });

    const celldata = [];
    const locCol  = 1;
    const nomCol  = includeNominal ? 2 : -1;
    const slStart = includeNominal ? 3 : 2;
    const overallCol = slStart + colCount;

    const colLetter = (idx) => {
      let s = '';
      let n = idx;
      while (n >= 0) {
        s = String.fromCharCode(65 + (n % 26)) + s;
        n = Math.floor(n / 26) - 1;
      }
      return s;
    };

    // Row 0 — headers. Overall col is a "spacer" (3-side nil, left
    // border retained so it matches the right edge of the data area).
    celldata.push({ r: 0, c: 0, v: HEADER(groupHeader) });
    celldata.push({ r: 0, c: locCol, v: HEADER('Location') });
    if (includeNominal) celldata.push({ r: 0, c: nomCol, v: HEADER('Nominal value (mm)') });
    for (let i = 0; i < colCount; i++) {
      celldata.push({ r: 0, c: slStart + i, v: HEADER(`${colPrefix}${i + 1}`) });
    }
    celldata.push({ r: 0, c: overallCol, v: OVERALL_SPACER() });

    const firstPosLet = colLetter(slStart);
    const lastPosLet  = colLetter(slStart + colCount - 1);

    let cursor = 1;
    pipes.forEach((pipe, pi) => {
      // `--` separator row between consecutive pipes.
      if (pi > 0) {
        celldata.push({ r: cursor, c: 0, v: SEPARATOR() });
        cursor += 1;
      }

      // Data rows for this pipe.
      const dataStartRow = cursor;
      pipe.locations.forEach((loc, mi) => {
        celldata.push({
          r: cursor, c: 0,
          v: mi === 0 ? HEADER(pipe.label) : STAR_HDR(),
        });
        celldata.push({ r: cursor, c: locCol, v: HEADER(loc) });
        if (includeNominal && mi > 0) {
          celldata.push({ r: cursor, c: nomCol, v: STAR() });
        }
        celldata.push({ r: cursor, c: overallCol, v: OVERALL_SPACER() });
        cursor += 1;
      });
      const dataEndRow = cursor - 1;
      const dataStartDisp = dataStartRow + 1;
      const dataEndDisp = dataEndRow + 1;

      // Dedicated label row between data and summary block. Cells are
      // chosen by looking at what sits in the same column on the FIRST
      // summary row immediately below:
      //   - col 0           : * (continues the group-label vertical merge)
      //   - col 1 (Location): "Statistics" header (because below is
      //                       "Lowest thk" — the first summary label)
      //   - col 2 (Nominal) : -* (mirrors the summary nominal sentinel)
      //   - col 3..lastPos  : ⇩ arrow (sits above formula cells)
      //   - Overall col     : bold "Overall" header
      celldata.push({ r: cursor, c: 0, v: STAR_HDR() });
      celldata.push({ r: cursor, c: locCol, v: STATISTICS_LABEL() });
      if (includeNominal) celldata.push({ r: cursor, c: nomCol, v: H_LEFT() });
      for (let i = 0; i < colCount; i += 1) {
        celldata.push({ r: cursor, c: slStart + i, v: ARROW_CELL() });
      }
      celldata.push({ r: cursor, c: overallCol, v: OVERALL_LABEL() });
      cursor += 1;

      // Helper to emit a summary row. Now also emits the Overall cell.
      // Summary labels in col B carry ht:1 so they render left-aligned
      // (col-0 cells are already left-aligned by default).
      function emitSummary(label, perColFormula, overallFormula) {
        celldata.push({ r: cursor, c: 0, v: STAR_HDR() });
        celldata.push({ r: cursor, c: locCol, v: HEADER(label, { ht: 1 }) });
        if (includeNominal) celldata.push({ r: cursor, c: nomCol, v: H_LEFT() });
        for (let i = 0; i < colCount; i++) {
          const col = colLetter(slStart + i);
          celldata.push({
            r: cursor, c: slStart + i,
            v: perColFormula(col),
          });
        }
        celldata.push({ r: cursor, c: overallCol, v: overallFormula() });
        cursor += 1;
      }

      emitSummary(
        'Lowest thk',
        (col) => FORMULA(`=MIN(${col}${dataStartDisp}:${col}${dataEndDisp})`),
        () => FORMULA(`=MIN(${firstPosLet}${cursor + 1}:${lastPosLet}${cursor + 1})`),
      );
      emitSummary(
        'Highest thk',
        (col) => FORMULA(`=MAX(${col}${dataStartDisp}:${col}${dataEndDisp})`),
        () => FORMULA(`=MAX(${firstPosLet}${cursor + 1}:${lastPosLet}${cursor + 1})`),
      );
      const avgRow = cursor;
      emitSummary(
        'Average thk',
        (col) => FORMULA(`=AVERAGE(${col}${dataStartDisp}:${col}${dataEndDisp})`),
        () => FORMULA(`=AVERAGE(${firstPosLet}${cursor + 1}:${lastPosLet}${cursor + 1})`),
      );

      if (includeNominal) {
        const avgDisp = avgRow + 1;
        const nomLetter = colLetter(nomCol);
        const nomRowDisp = dataStartRow + 1;  // the only cell with the actual nominal value
        celldata.push({ r: cursor, c: 0, v: STAR_HDR() });
        celldata.push({ r: cursor, c: locCol, v: HEADER('Average degradation', { ht: 1 }) });
        celldata.push({ r: cursor, c: nomCol, v: H_LEFT() });
        for (let i = 0; i < colCount; i++) {
          const col = colLetter(slStart + i);
          celldata.push({
            r: cursor, c: slStart + i,
            v: PERCENT(`=IFERROR((${nomLetter}${nomRowDisp}-${col}${avgDisp})/${nomLetter}${nomRowDisp},"-")`),
          });
        }
        celldata.push({
          r: cursor, c: overallCol,
          v: PERCENT(`=AVERAGE(${firstPosLet}${cursor + 1}:${lastPosLet}${cursor + 1})`),
        });
        cursor += 1;
      }
    });

    return [{
      name: 'UT Measurements',
      celldata,
      row: Math.max(50, cursor + 5),
      column: Math.max(26, overallCol + 2),
    }];
  }

  // ── Build shell-course template (specialType: 'shell-course') ──
  // Standalone builder for Shell (External / Internal) → Shell courses.
  // Deliberately NOT delegating to buildPipeSegmentTemplate so the
  // shell-course layout can evolve independently of pipes.
  //
  // courses: [{ label: 'Shell Course 1', locations: ['30 (i)', '(ii)', …, 'Top (n)'] }, …]
  //
  // Per-course structure (mirrors the pipe-segment treatment v3.1.11+):
  //   - Col 0 ('Shell Course'): course label on the first data row;
  //     subsequent rows of the block (data + summary) carry `*`
  //     (vertical-merge sentinel) so the course label visually spans
  //     the entire block.
  //   - Col 1 ('Location'): per-location label per data row; per-row
  //     summary label ('Lowest thk' / 'Highest thk' / 'Average thk' /
  //     'Average degradation') on the summary rows.
  //   - Col 2 ('Nominal value (mm)', when included): user enters the
  //     value ONCE on the first row of the course's data range; the
  //     subsequent data rows carry `*` (vertical merge) so the value
  //     visually merges down. Summary rows carry `-*` (horizontal
  //     merge LEFT) so the summary label in col B extends across into
  //     col C.
  //   - Col 3+ (POS / SL columns): user-entry data cells. Summary
  //     rows auto-emit per-column MIN / MAX / AVERAGE formulas over
  //     the course's data range. With nominal, an Average degradation
  //     row also emits =IFERROR((nominal − avg) / nominal, "-") in
  //     percent format — referencing THE COURSE'S OWN nominal cell.
  //
  // A `--` separator row sits between consecutive courses so the
  // renderer (web preview + Word PDF) splits each course into its own
  // visual sub-table with the headers repeated.
  function buildShellCourseTemplate({ courses, includeNominal, colPrefix, colCount }) {
    const HEADER = (val, opts = {}) => ({ v: val, m: String(val), ff: ARIAL_FF, bl: 1, ct: { fa: 'General', t: 'g' }, ...opts });
    const STAR_HDR = () => ({ v: '*', m: '*', ff: ARIAL_FF, bl: 1, ct: { fa: 'General', t: 'g' } });
    const STAR = () => ({ v: '*', m: '*', ff: ARIAL_FF });
    const H_LEFT = () => ({ v: '-*', m: '-*', ff: ARIAL_FF });
    const SEPARATOR = () => ({ v: '--', m: '--', ff: ARIAL_FF });
    const OVERALL_LABEL = () => ({ v: 'Overall', m: 'Overall', ff: ARIAL_FF, bl: 1, borderless: true, ct: { fa: 'General', t: 'g' } });
    const OVERALL_SPACER = () => ({ v: '', m: '', ff: ARIAL_FF, borderless: true, whiteBg: true });
    const ARROW_CELL = () => ({ v: '⇩', m: '⇩', ff: ARIAL_FF, ht: 0, headerBg: true, noLeftBorder: true, noRightBorder: true });
    const STATISTICS_LABEL = () => ({ v: ' ', m: ' ', ff: ARIAL_FF, bl: 1, ht: 1, headerBg: true, noLeftBorder: true, noRightBorder: true, ct: { fa: 'General', t: 'g' } });
    const PERCENT = (formula) => ({
      f: formula, m: '', ff: ARIAL_FF,
      ct: { fa: '0.0%', t: 'n' },
    });
    const FORMULA = (formula) => ({
      f: formula, m: '', ff: ARIAL_FF,
      ct: { fa: 'General', t: 'n' },
    });

    const celldata = [];
    const locCol  = 1;
    const nomCol  = includeNominal ? 2 : -1;
    const slStart = includeNominal ? 3 : 2;
    const overallCol = slStart + colCount;

    const colLetter = (idx) => {
      let s = '';
      let n = idx;
      while (n >= 0) {
        s = String.fromCharCode(65 + (n % 26)) + s;
        n = Math.floor(n / 26) - 1;
      }
      return s;
    };

    // Row 0 — headers. Overall col uses the spacer (3-side nil).
    celldata.push({ r: 0, c: 0, v: HEADER('Shell Course') });
    celldata.push({ r: 0, c: locCol, v: HEADER('Location') });
    if (includeNominal) celldata.push({ r: 0, c: nomCol, v: HEADER('Nominal value (mm)') });
    for (let i = 0; i < colCount; i++) {
      celldata.push({ r: 0, c: slStart + i, v: HEADER(`${colPrefix}${i + 1}`) });
    }
    celldata.push({ r: 0, c: overallCol, v: OVERALL_SPACER() });

    const firstPosLet = colLetter(slStart);
    const lastPosLet  = colLetter(slStart + colCount - 1);

    let cursor = 1;
    courses.forEach((course, ci) => {
      // `--` separator row between consecutive courses.
      if (ci > 0) {
        celldata.push({ r: cursor, c: 0, v: SEPARATOR() });
        cursor += 1;
      }

      // Data rows for this course.
      const dataStartRow = cursor;
      course.locations.forEach((loc, mi) => {
        celldata.push({
          r: cursor, c: 0,
          v: mi === 0 ? HEADER(course.label) : STAR_HDR(),
        });
        celldata.push({ r: cursor, c: locCol, v: HEADER(loc) });
        if (includeNominal && mi > 0) {
          celldata.push({ r: cursor, c: nomCol, v: STAR() });
        }
        celldata.push({ r: cursor, c: overallCol, v: OVERALL_SPACER() });
        cursor += 1;
      });
      const dataEndRow = cursor - 1;
      const dataStartDisp = dataStartRow + 1;
      const dataEndDisp = dataEndRow + 1;

      // Dedicated label row between data and summary block. Cells
      // chosen by the smart rule (see pipe builder for full rules).
      celldata.push({ r: cursor, c: 0, v: STAR_HDR() });
      celldata.push({ r: cursor, c: locCol, v: STATISTICS_LABEL() });
      if (includeNominal) celldata.push({ r: cursor, c: nomCol, v: H_LEFT() });
      for (let i = 0; i < colCount; i += 1) {
        celldata.push({ r: cursor, c: slStart + i, v: ARROW_CELL() });
      }
      celldata.push({ r: cursor, c: overallCol, v: OVERALL_LABEL() });
      cursor += 1;

      // Summary-row emitter. `*` in col A continues the course label
      // vertical merge; `-*` in nominal col makes col B's summary
      // label visually merge across into col C; Overall col gets the
      // cross-column aggregate formula. Summary labels use ht:1.
      function emitSummary(label, perColFormula, overallFormula) {
        celldata.push({ r: cursor, c: 0, v: STAR_HDR() });
        celldata.push({ r: cursor, c: locCol, v: HEADER(label, { ht: 1 }) });
        if (includeNominal) celldata.push({ r: cursor, c: nomCol, v: H_LEFT() });
        for (let i = 0; i < colCount; i++) {
          const col = colLetter(slStart + i);
          celldata.push({
            r: cursor, c: slStart + i,
            v: perColFormula(col),
          });
        }
        celldata.push({ r: cursor, c: overallCol, v: overallFormula() });
        cursor += 1;
      }

      emitSummary(
        'Lowest thk',
        (col) => FORMULA(`=MIN(${col}${dataStartDisp}:${col}${dataEndDisp})`),
        () => FORMULA(`=MIN(${firstPosLet}${cursor + 1}:${lastPosLet}${cursor + 1})`),
      );
      emitSummary(
        'Highest thk',
        (col) => FORMULA(`=MAX(${col}${dataStartDisp}:${col}${dataEndDisp})`),
        () => FORMULA(`=MAX(${firstPosLet}${cursor + 1}:${lastPosLet}${cursor + 1})`),
      );
      const avgRow = cursor;
      emitSummary(
        'Average thk',
        (col) => FORMULA(`=AVERAGE(${col}${dataStartDisp}:${col}${dataEndDisp})`),
        () => FORMULA(`=AVERAGE(${firstPosLet}${cursor + 1}:${lastPosLet}${cursor + 1})`),
      );

      if (includeNominal) {
        const avgDisp = avgRow + 1;
        const nomLetter = colLetter(nomCol);
        const nomRowDisp = dataStartRow + 1;  // only cell with the actual nominal value
        celldata.push({ r: cursor, c: 0, v: STAR_HDR() });
        celldata.push({ r: cursor, c: locCol, v: HEADER('Average degradation', { ht: 1 }) });
        celldata.push({ r: cursor, c: nomCol, v: H_LEFT() });
        for (let i = 0; i < colCount; i++) {
          const col = colLetter(slStart + i);
          celldata.push({
            r: cursor, c: slStart + i,
            v: PERCENT(`=IFERROR((${nomLetter}${nomRowDisp}-${col}${avgDisp})/${nomLetter}${nomRowDisp},"-")`),
          });
        }
        celldata.push({
          r: cursor, c: overallCol,
          v: PERCENT(`=AVERAGE(${firstPosLet}${cursor + 1}:${lastPosLet}${cursor + 1})`),
        });
        cursor += 1;
      }
    });

    return [{
      name: 'UT Measurements',
      celldata,
      row: Math.max(50, cursor + 5),
      column: Math.max(26, overallCol + 2),
    }];
  }

  // (buildSupportingColumnTemplate retired in v3.1.15 — supporting-
  // column now reuses buildPipeSegmentTemplate with groupHeader:
  // 'Column' and per-column location counts, mirroring the pipe-
  // segment configuration UX.)

  // ── Build "Outside edge of bottom" transposed template ────────────
  // Locations (1-3 checked options) become COLUMN HEADERS across the
  // top, scanlines (SL1..SLn) run DOWN the first column. Summary rows
  // ("Lowest thk", "Highest thk", "Average thk", and optionally
  // "Average degradation") are auto-added below the data with live
  // formulas per location column.
  //
  // Without nominal:
  //   Row 1: A1="Location", B1..M1=location names
  //   Rows 2..N+1: A=SLn label, B..M=user entry
  //   Row N+2: A="Lowest thk", B..M = MIN over data range
  //   Row N+3: A="Highest thk", B..M = MAX
  //   Row N+4: A="Average thk", B..M = AVERAGE
  //
  // With nominal: nominal row inserted at row 2; data range shifts
  // one row down; an extra "Average degradation" row appended with
  // =IFERROR((nominal-avg)/nominal,"-") percent-formatted.
  function buildOutsideEdgeBottomTemplate({ locations, includeNominal, colPrefix, colCount }) {
    // colPrefix / colCount unused here — they describe the scanline-
    // column structure of the OLD layout. In the transposed layout we
    // always use "SL" for the row labels and the scanline count comes
    // from colCount.
    void colPrefix;

    const HEADER = (val, opts = {}) => ({ v: val, m: String(val), ff: ARIAL_FF, bl: 1, ct: { fa: 'General', t: 'g' }, ...opts });
    const OVERALL_LABEL = () => ({ v: 'Overall', m: 'Overall', ff: ARIAL_FF, bl: 1, borderless: true, ct: { fa: 'General', t: 'g' } });
    const OVERALL_SPACER = () => ({ v: '', m: '', ff: ARIAL_FF, borderless: true, whiteBg: true });
    const ARROW_CELL = () => ({ v: '⇩', m: '⇩', ff: ARIAL_FF, ht: 0, headerBg: true, noLeftBorder: true, noRightBorder: true });
    const STATISTICS_LABEL = () => ({ v: ' ', m: ' ', ff: ARIAL_FF, bl: 1, ht: 1, headerBg: true, noLeftBorder: true, noRightBorder: true, ct: { fa: 'General', t: 'g' } });
    const PERCENT = (formula) => ({
      f: formula,
      m: '',
      ff: ARIAL_FF,
      ct: { fa: '0.0%', t: 'n' },
    });
    const FORMULA = (formula) => ({
      f: formula,
      m: '',
      ff: ARIAL_FF,
      ct: { fa: 'General', t: 'n' },
    });

    const M = locations.length;        // # location columns (data starts at col 1)
    const N = colCount;                 // # scanlines (SL1..SLn)
    const celldata = [];
    const overallCol = 1 + M;          // Overall column index (rightmost)

    // Convert 0-based column index → letter sequence (A, B, ..., Z, AA…).
    const colLetter = (idx) => {
      let s = '';
      let n = idx;
      while (n >= 0) {
        s = String.fromCharCode(65 + (n % 26)) + s;
        n = Math.floor(n / 26) - 1;
      }
      return s;
    };

    // Row 0 (display row 1): A = "Location", B..M = location headers,
    // Overall col = spacer (3-side nil).
    celldata.push({ r: 0, c: 0, v: HEADER('Location') });
    locations.forEach((loc, i) => {
      celldata.push({ r: 0, c: 1 + i, v: HEADER(loc) });
    });
    celldata.push({ r: 0, c: overallCol, v: OVERALL_SPACER() });

    // Compute row positions.
    let cursor = 1;                      // 0-indexed row cursor
    let nominalRowDisp = null;           // 1-indexed for formulas
    if (includeNominal) {
      celldata.push({ r: cursor, c: 0, v: HEADER('Nominal value (mm)') });
      celldata.push({ r: cursor, c: overallCol, v: OVERALL_SPACER() });
      nominalRowDisp = cursor + 1;
      cursor += 1;
    }
    const dataStartRow = cursor;         // 0-indexed
    for (let i = 0; i < N; i += 1) {
      celldata.push({ r: cursor, c: 0, v: HEADER(`SL${i + 1}`) });
      celldata.push({ r: cursor, c: overallCol, v: OVERALL_SPACER() });
      cursor += 1;
    }
    const dataEndRow = cursor - 1;       // last data row, 0-indexed
    const dataStartDisp = dataStartRow + 1;
    const dataEndDisp = dataEndRow + 1;

    // Dedicated label row between data and summary. Cells chosen by
    // smart rule: col 0 = "Statistics" (below is "Lowest thk"),
    // cols 1..M = ⇩ arrow (sits above formula cells), Overall col =
    // bold "Overall" header.
    celldata.push({ r: cursor, c: 0, v: STATISTICS_LABEL() });
    for (let i = 0; i < M; i += 1) {
      celldata.push({ r: cursor, c: 1 + i, v: ARROW_CELL() });
    }
    celldata.push({ r: cursor, c: overallCol, v: OVERALL_LABEL() });
    cursor += 1;

    const firstLet = colLetter(1);
    const lastLet  = colLetter(M);

    // Summary rows: Lowest thk, Highest thk, Average thk. The col-0
    // label cells carry ht:1 (left-aligned) — even though col 0 is
    // left-aligned by default, the explicit flag means "always left"
    // regardless of any renderer's default rule.
    const lowestRow = cursor;
    celldata.push({ r: cursor, c: 0, v: HEADER('Lowest thk', { ht: 1 }) });
    for (let i = 0; i < M; i += 1) {
      const col = colLetter(1 + i);
      celldata.push({
        r: cursor, c: 1 + i,
        v: FORMULA(`=MIN(${col}${dataStartDisp}:${col}${dataEndDisp})`),
      });
    }
    celldata.push({
      r: cursor, c: overallCol,
      v: FORMULA(`=MIN(${firstLet}${cursor + 1}:${lastLet}${cursor + 1})`),
    });
    cursor += 1;

    const highestRow = cursor;
    celldata.push({ r: cursor, c: 0, v: HEADER('Highest thk', { ht: 1 }) });
    for (let i = 0; i < M; i += 1) {
      const col = colLetter(1 + i);
      celldata.push({
        r: cursor, c: 1 + i,
        v: FORMULA(`=MAX(${col}${dataStartDisp}:${col}${dataEndDisp})`),
      });
    }
    celldata.push({
      r: cursor, c: overallCol,
      v: FORMULA(`=MAX(${firstLet}${cursor + 1}:${lastLet}${cursor + 1})`),
    });
    cursor += 1;

    const avgRow = cursor;
    celldata.push({ r: cursor, c: 0, v: HEADER('Average thk', { ht: 1 }) });
    for (let i = 0; i < M; i += 1) {
      const col = colLetter(1 + i);
      celldata.push({
        r: cursor, c: 1 + i,
        v: FORMULA(`=AVERAGE(${col}${dataStartDisp}:${col}${dataEndDisp})`),
      });
    }
    celldata.push({
      r: cursor, c: overallCol,
      v: FORMULA(`=AVERAGE(${firstLet}${cursor + 1}:${lastLet}${cursor + 1})`),
    });
    cursor += 1;

    // Average degradation row (only if nominal present).
    if (includeNominal && nominalRowDisp != null) {
      celldata.push({ r: cursor, c: 0, v: HEADER('Average degradation', { ht: 1 }) });
      const avgDisp = avgRow + 1;
      for (let i = 0; i < M; i += 1) {
        const col = colLetter(1 + i);
        celldata.push({
          r: cursor, c: 1 + i,
          v: PERCENT(`=IFERROR((${col}${nominalRowDisp}-${col}${avgDisp})/${col}${nominalRowDisp},"-")`),
        });
      }
      celldata.push({
        r: cursor, c: overallCol,
        v: PERCENT(`=AVERAGE(${firstLet}${cursor + 1}:${lastLet}${cursor + 1})`),
      });
      cursor += 1;
    }
    void lowestRow; void highestRow;     // silence unused-var lints

    return [{
      name: 'UT Measurements',
      celldata,
      row: Math.max(50, cursor + 5),
      column: Math.max(26, overallCol + 2),
    }];
  }

  // ── Build Annular plates template (specialType: 'annular-plates') ──
  // Same horizontal/vertical shape as the generic layout (Plate # in
  // column A, POS columns to its right) BUT:
  //  - nominal value moves from a column to its OWN row at row 2,
  //    label "Nominal value (mm)" in A2. The user types the nominal
  //    once into B2; C2..(M+1)2 get the `-*` sentinel so the row
  //    visually renders as one merged cell carrying B2's value.
  //  - summary rows Lowest / Highest / Average thk auto-emit MIN /
  //    MAX / AVERAGE formulas per POS column over the plate-number
  //    rows (skipping the nominal row).
  //  - if nominal is included, an extra "Average degradation" row
  //    auto-emits =IFERROR((B2 - <col><avgRow>) / B2, "-") percent-
  //    formatted in every POS column — every column references B2
  //    for the nominal since it's the only cell with the real value.
  function buildAnnularPlatesTemplate({ rowLabels, rowHeader, includeNominal, colPrefix, colCount }) {
    const HEADER = (val, opts = {}) => ({ v: val, m: String(val), ff: ARIAL_FF, bl: 1, ct: { fa: 'General', t: 'g' }, ...opts });
    const OVERALL_LABEL = () => ({ v: 'Overall', m: 'Overall', ff: ARIAL_FF, bl: 1, borderless: true, ct: { fa: 'General', t: 'g' } });
    const OVERALL_SPACER = () => ({ v: '', m: '', ff: ARIAL_FF, borderless: true, whiteBg: true });
    const ARROW_CELL = () => ({ v: '⇩', m: '⇩', ff: ARIAL_FF, ht: 0, headerBg: true, noLeftBorder: true, noRightBorder: true });
    const STATISTICS_LABEL = () => ({ v: ' ', m: ' ', ff: ARIAL_FF, bl: 1, ht: 1, headerBg: true, noLeftBorder: true, noRightBorder: true, ct: { fa: 'General', t: 'g' } });
    const SENTINEL = () => ({ v: '-*', m: '-*', ff: ARIAL_FF });
    const PERCENT = (formula) => ({
      f: formula,
      m: '',
      ff: ARIAL_FF,
      ct: { fa: '0.0%', t: 'n' },
    });
    const FORMULA = (formula) => ({
      f: formula,
      m: '',
      ff: ARIAL_FF,
      ct: { fa: 'General', t: 'n' },
    });

    const M = colCount;                  // # POS columns
    const celldata = [];
    const overallCol = 1 + M;            // Overall column index

    // 0-based column index → letter sequence.
    const colLetter = (idx) => {
      let s = '';
      let n = idx;
      while (n >= 0) {
        s = String.fromCharCode(65 + (n % 26)) + s;
        n = Math.floor(n / 26) - 1;
      }
      return s;
    };

    // Row 0 — headers: A=rowHeader, B..(M+1)=POSn, Overall = spacer.
    celldata.push({ r: 0, c: 0, v: HEADER(rowHeader) });
    for (let i = 0; i < M; i += 1) {
      celldata.push({ r: 0, c: 1 + i, v: HEADER(`${colPrefix}${i + 1}`) });
    }
    celldata.push({ r: 0, c: overallCol, v: OVERALL_SPACER() });

    let cursor = 1;                       // 0-indexed row cursor
    let nominalRowDisp = null;            // 1-indexed (for formulas)

    // Optional nominal row at row 2 (cursor=1, 1-indexed = 2).
    if (includeNominal) {
      celldata.push({ r: cursor, c: 0, v: HEADER('Nominal value (mm)') });
      // B2 is left intentionally blank — user enters the nominal value.
      // C2..(M+1)2 get the `-*` sentinel so renderer merges them with B2.
      for (let i = 1; i < M; i += 1) {
        celldata.push({ r: cursor, c: 1 + i, v: SENTINEL() });
      }
      celldata.push({ r: cursor, c: overallCol, v: OVERALL_SPACER() });
      nominalRowDisp = cursor + 1;
      cursor += 1;
    }

    // Plate-number rows.
    const dataStartRow = cursor;
    rowLabels.forEach((label) => {
      celldata.push({ r: cursor, c: 0, v: HEADER(label) });
      celldata.push({ r: cursor, c: overallCol, v: OVERALL_SPACER() });
      cursor += 1;
    });
    const dataEndRow = cursor - 1;
    const dataStartDisp = dataStartRow + 1;
    const dataEndDisp = dataEndRow + 1;

    // Dedicated label row between data and summary. Cells by rule:
    // col 0 = "Statistics" (below is "Lowest thk"); cols 1..M = ⇩
    // arrow; Overall col = bold "Overall".
    celldata.push({ r: cursor, c: 0, v: STATISTICS_LABEL() });
    for (let i = 0; i < M; i += 1) {
      celldata.push({ r: cursor, c: 1 + i, v: ARROW_CELL() });
    }
    celldata.push({ r: cursor, c: overallCol, v: OVERALL_LABEL() });
    cursor += 1;

    function emitSummaryRow(label, perColFormula, overallFormula) {
      celldata.push({ r: cursor, c: 0, v: HEADER(label, { ht: 1 }) });
      for (let i = 0; i < M; i += 1) {
        const col = colLetter(1 + i);
        celldata.push({
          r: cursor, c: 1 + i,
          v: perColFormula(col),
        });
      }
      celldata.push({ r: cursor, c: overallCol, v: overallFormula() });
      cursor += 1;
    }

    const firstLet = colLetter(1);
    const lastLet  = colLetter(M);

    // Summary rows. Overall formulas reference the per-col summary
    // cells on the same row (cursor + 1 == 1-indexed row number).
    emitSummaryRow(
      'Lowest thk',
      (col) => FORMULA(`=MIN(${col}${dataStartDisp}:${col}${dataEndDisp})`),
      () => FORMULA(`=MIN(${firstLet}${cursor + 1}:${lastLet}${cursor + 1})`),
    );
    emitSummaryRow(
      'Highest thk',
      (col) => FORMULA(`=MAX(${col}${dataStartDisp}:${col}${dataEndDisp})`),
      () => FORMULA(`=MAX(${firstLet}${cursor + 1}:${lastLet}${cursor + 1})`),
    );
    const avgRow = cursor;
    emitSummaryRow(
      'Average thk',
      (col) => FORMULA(`=AVERAGE(${col}${dataStartDisp}:${col}${dataEndDisp})`),
      () => FORMULA(`=AVERAGE(${firstLet}${cursor + 1}:${lastLet}${cursor + 1})`),
    );

    // Average degradation row — only when nominal present. EVERY
    // POS column's degradation references B<nomRow> for the nominal.
    if (includeNominal && nominalRowDisp != null) {
      const avgDisp = avgRow + 1;
      emitSummaryRow(
        'Average degradation',
        (col) => PERCENT(`=IFERROR((B${nominalRowDisp}-${col}${avgDisp})/B${nominalRowDisp},"-")`),
        () => PERCENT(`=AVERAGE(${firstLet}${cursor + 1}:${lastLet}${cursor + 1})`),
      );
    }

    return [{
      name: 'UT Measurements',
      celldata,
      row: Math.max(50, cursor + 5),
      column: Math.max(26, overallCol + 2),
    }];
  }

  // ── Build "Flat bottom" template (specialType: 'flat-bottom') ────
  // Hybrid of layout #1 + #2:
  //   - TRANSPOSED shape (locations as column headers, scanlines as
  //     row labels) — same as Outside edge of bottom.
  //   - Nominal row uses the merge-left pattern from Annular plates:
  //     B<nomRow> is the single user-entry cell; C..(M+1)<nomRow>
  //     carry `-*` sentinels so the rendered preview/PDF show one
  //     merged cell across the row.
  //   - Summary rows (Lowest / Highest / Average thk) below the
  //     scanline data with MIN/MAX/AVERAGE per location column.
  //   - If nominal: extra "Average degradation" row, EVERY column
  //     references B<nomRow> for the nominal value (matching #2).
  // ── Corner-weld template (CW-OSI sub-card) ────────────────────────
  // Transposed grid like flat-bottom (locations across, scanlines down
  // column A) with summary rows (Min / Max / Average / optional Average
  // degradation). The Overall column is only emitted when there's more
  // than one location — for a single-location table the per-location
  // summary IS the overall.
  function buildCornerWeldTemplate({ rowLabels, includeNominal, colPrefix, colCount }) {
    const HEADER = (val, opts = {}) => ({ v: val, m: String(val), ff: ARIAL_FF, bl: 1, ct: { fa: 'General', t: 'g' }, ...opts });
    const OVERALL_LABEL = () => ({ v: 'Overall', m: 'Overall', ff: ARIAL_FF, bl: 1, borderless: true, ct: { fa: 'General', t: 'g' } });
    const OVERALL_SPACER = () => ({ v: '', m: '', ff: ARIAL_FF, borderless: true, whiteBg: true });
    const ARROW_CELL = () => ({ v: '⇩', m: '⇩', ff: ARIAL_FF, ht: 0, headerBg: true, noLeftBorder: true, noRightBorder: true });
    const STATISTICS_LABEL = () => ({ v: ' ', m: ' ', ff: ARIAL_FF, bl: 1, ht: 1, headerBg: true, noLeftBorder: true, noRightBorder: true, ct: { fa: 'General', t: 'g' } });
    const SENTINEL = () => ({ v: '-*', m: '-*', ff: ARIAL_FF });
    const PERCENT = (formula) => ({ f: formula, m: '', ff: ARIAL_FF, ct: { fa: '0.0%', t: 'n' } });
    const FORMULA = (formula) => ({ f: formula, m: '', ff: ARIAL_FF, ct: { fa: 'General', t: 'n' } });

    const locations = rowLabels;
    const M = locations.length;
    const N = colCount;
    const showOverall = M > 1;
    const overallCol = 1 + M; // only used when showOverall
    const celldata = [];

    const colLetter = (idx) => {
      let s = '';
      let n = idx;
      while (n >= 0) {
        s = String.fromCharCode(65 + (n % 26)) + s;
        n = Math.floor(n / 26) - 1;
      }
      return s;
    };

    // Row 0: A = "Location", B..M+1 = location labels, (Overall spacer)
    celldata.push({ r: 0, c: 0, v: HEADER('Location') });
    locations.forEach((loc, i) => {
      celldata.push({ r: 0, c: 1 + i, v: HEADER(loc) });
    });
    if (showOverall) celldata.push({ r: 0, c: overallCol, v: OVERALL_SPACER() });

    let cursor = 1;
    let nominalRowDisp = null;

    // Optional nominal row, merge-left pattern.
    if (includeNominal) {
      celldata.push({ r: cursor, c: 0, v: HEADER('Nominal value (mm)') });
      for (let i = 1; i < M; i += 1) {
        celldata.push({ r: cursor, c: 1 + i, v: SENTINEL() });
      }
      if (showOverall) celldata.push({ r: cursor, c: overallCol, v: OVERALL_SPACER() });
      nominalRowDisp = cursor + 1;
      cursor += 1;
    }

    // SL rows down column A. Measurement cells blank for user entry.
    const dataStartRow = cursor;
    for (let i = 0; i < N; i += 1) {
      celldata.push({ r: cursor, c: 0, v: HEADER(`${colPrefix}${i + 1}`) });
      if (showOverall) celldata.push({ r: cursor, c: overallCol, v: OVERALL_SPACER() });
      cursor += 1;
    }
    const dataEndRow = cursor - 1;
    const dataStartDisp = dataStartRow + 1;
    const dataEndDisp = dataEndRow + 1;

    // Statistics arrow row.
    celldata.push({ r: cursor, c: 0, v: STATISTICS_LABEL() });
    for (let i = 0; i < M; i += 1) {
      celldata.push({ r: cursor, c: 1 + i, v: ARROW_CELL() });
    }
    if (showOverall) celldata.push({ r: cursor, c: overallCol, v: OVERALL_LABEL() });
    cursor += 1;

    const firstLet = colLetter(1);
    const lastLet  = colLetter(M);

    function emitSummaryRow(label, perColFormula, overallFormula) {
      celldata.push({ r: cursor, c: 0, v: HEADER(label, { ht: 1 }) });
      for (let i = 0; i < M; i += 1) {
        const col = colLetter(1 + i);
        celldata.push({ r: cursor, c: 1 + i, v: perColFormula(col) });
      }
      if (showOverall) celldata.push({ r: cursor, c: overallCol, v: overallFormula() });
      cursor += 1;
    }

    emitSummaryRow(
      'Min',
      (col) => FORMULA(`=MIN(${col}${dataStartDisp}:${col}${dataEndDisp})`),
      () => FORMULA(`=MIN(${firstLet}${cursor + 1}:${lastLet}${cursor + 1})`),
    );
    emitSummaryRow(
      'Max',
      (col) => FORMULA(`=MAX(${col}${dataStartDisp}:${col}${dataEndDisp})`),
      () => FORMULA(`=MAX(${firstLet}${cursor + 1}:${lastLet}${cursor + 1})`),
    );
    const avgRow = cursor;
    emitSummaryRow(
      'Average',
      (col) => FORMULA(`=AVERAGE(${col}${dataStartDisp}:${col}${dataEndDisp})`),
      () => FORMULA(`=AVERAGE(${firstLet}${cursor + 1}:${lastLet}${cursor + 1})`),
    );

    if (includeNominal && nominalRowDisp != null) {
      const avgDisp = avgRow + 1;
      emitSummaryRow(
        'Average degradation',
        (col) => PERCENT(`=IFERROR((B${nominalRowDisp}-${col}${avgDisp})/B${nominalRowDisp},"-")`),
        () => PERCENT(`=AVERAGE(${firstLet}${cursor + 1}:${lastLet}${cursor + 1})`),
      );
    }

    return [{
      name: 'Measurements',
      celldata,
      row: Math.max(50, cursor + 5),
      column: Math.max(26, (showOverall ? overallCol : M) + 2),
    }];
  }

  function buildFlatBottomTemplate({ rowLabels, includeNominal, colPrefix, colCount }) {
    const HEADER = (val, opts = {}) => ({ v: val, m: String(val), ff: ARIAL_FF, bl: 1, ct: { fa: 'General', t: 'g' }, ...opts });
    const OVERALL_LABEL = () => ({ v: 'Overall', m: 'Overall', ff: ARIAL_FF, bl: 1, borderless: true, ct: { fa: 'General', t: 'g' } });
    const OVERALL_SPACER = () => ({ v: '', m: '', ff: ARIAL_FF, borderless: true, whiteBg: true });
    const ARROW_CELL = () => ({ v: '⇩', m: '⇩', ff: ARIAL_FF, ht: 0, headerBg: true, noLeftBorder: true, noRightBorder: true });
    const STATISTICS_LABEL = () => ({ v: ' ', m: ' ', ff: ARIAL_FF, bl: 1, ht: 1, headerBg: true, noLeftBorder: true, noRightBorder: true, ct: { fa: 'General', t: 'g' } });
    const SENTINEL = () => ({ v: '-*', m: '-*', ff: ARIAL_FF });
    const PERCENT = (formula) => ({
      f: formula,
      m: '',
      ff: ARIAL_FF,
      ct: { fa: '0.0%', t: 'n' },
    });
    const FORMULA = (formula) => ({
      f: formula,
      m: '',
      ff: ARIAL_FF,
      ct: { fa: 'General', t: 'n' },
    });

    const locations = rowLabels;          // location labels become column headers
    const M = locations.length;
    const N = colCount;                   // # scanlines (rows down col A)
    const celldata = [];
    const overallCol = 1 + M;             // Overall column index

    const colLetter = (idx) => {
      let s = '';
      let n = idx;
      while (n >= 0) {
        s = String.fromCharCode(65 + (n % 26)) + s;
        n = Math.floor(n / 26) - 1;
      }
      return s;
    };

    // Row 0: A="Location", B..(M+1)=location labels, Overall = spacer.
    celldata.push({ r: 0, c: 0, v: HEADER('Location') });
    locations.forEach((loc, i) => {
      celldata.push({ r: 0, c: 1 + i, v: HEADER(loc) });
    });
    celldata.push({ r: 0, c: overallCol, v: OVERALL_SPACER() });

    let cursor = 1;
    let nominalRowDisp = null;

    // Optional nominal row with merge-left pattern.
    if (includeNominal) {
      celldata.push({ r: cursor, c: 0, v: HEADER('Nominal value (mm)') });
      for (let i = 1; i < M; i += 1) {
        celldata.push({ r: cursor, c: 1 + i, v: SENTINEL() });
      }
      celldata.push({ r: cursor, c: overallCol, v: OVERALL_SPACER() });
      nominalRowDisp = cursor + 1;
      cursor += 1;
    }

    // SL/POS row labels down column A.
    const dataStartRow = cursor;
    for (let i = 0; i < N; i += 1) {
      celldata.push({ r: cursor, c: 0, v: HEADER(`${colPrefix}${i + 1}`) });
      celldata.push({ r: cursor, c: overallCol, v: OVERALL_SPACER() });
      cursor += 1;
    }
    const dataEndRow = cursor - 1;
    const dataStartDisp = dataStartRow + 1;
    const dataEndDisp = dataEndRow + 1;

    // Dedicated label row between data and summary. Cells by rule:
    // col 0 = "Statistics" (below is "Lowest thk"); cols 1..M = ⇩
    // arrow; Overall col = bold "Overall".
    celldata.push({ r: cursor, c: 0, v: STATISTICS_LABEL() });
    for (let i = 0; i < M; i += 1) {
      celldata.push({ r: cursor, c: 1 + i, v: ARROW_CELL() });
    }
    celldata.push({ r: cursor, c: overallCol, v: OVERALL_LABEL() });
    cursor += 1;

    const firstLet = colLetter(1);
    const lastLet  = colLetter(M);

    function emitSummaryRow(label, perColFormula, overallFormula) {
      celldata.push({ r: cursor, c: 0, v: HEADER(label, { ht: 1 }) });
      for (let i = 0; i < M; i += 1) {
        const col = colLetter(1 + i);
        celldata.push({
          r: cursor, c: 1 + i,
          v: perColFormula(col),
        });
      }
      celldata.push({ r: cursor, c: overallCol, v: overallFormula() });
      cursor += 1;
    }

    emitSummaryRow(
      'Lowest thk',
      (col) => FORMULA(`=MIN(${col}${dataStartDisp}:${col}${dataEndDisp})`),
      () => FORMULA(`=MIN(${firstLet}${cursor + 1}:${lastLet}${cursor + 1})`),
    );
    emitSummaryRow(
      'Highest thk',
      (col) => FORMULA(`=MAX(${col}${dataStartDisp}:${col}${dataEndDisp})`),
      () => FORMULA(`=MAX(${firstLet}${cursor + 1}:${lastLet}${cursor + 1})`),
    );
    const avgRow = cursor;
    emitSummaryRow(
      'Average thk',
      (col) => FORMULA(`=AVERAGE(${col}${dataStartDisp}:${col}${dataEndDisp})`),
      () => FORMULA(`=AVERAGE(${firstLet}${cursor + 1}:${lastLet}${cursor + 1})`),
    );

    if (includeNominal && nominalRowDisp != null) {
      const avgDisp = avgRow + 1;
      emitSummaryRow(
        'Average degradation',
        (col) => PERCENT(`=IFERROR((B${nominalRowDisp}-${col}${avgDisp})/B${nominalRowDisp},"-")`),
        () => PERCENT(`=AVERAGE(${firstLet}${cursor + 1}:${lastLet}${cursor + 1})`),
      );
    }

    return [{
      name: 'UT Measurements',
      celldata,
      row: Math.max(50, cursor + 5),
      column: Math.max(26, overallCol + 2),
    }];
  }

  // ── Build a measurement table template from configure-layout output ──
  // rowLabels       : string[] — one entry per data row
  // rowHeader       : string   — column-A header cell text
  // includeNominal  : boolean  — whether to include the Nominal column
  // colPrefix       : 'SL' | 'POS'
  // colCount        : number   — how many measurement columns
  function buildLayoutTemplate({ rowLabels, rowHeader, includeNominal, colPrefix, colCount }) {
    const HEADER = (val) => ({ v: val, m: String(val), ff: ARIAL_FF, bl: 1, ct: { fa: 'General', t: 'g' } });
    const celldata = [];
    const nomCol   = includeNominal ? 1 : -1;
    const slStart  = includeNominal ? 2 : 1;

    // Row 0 — header row
    celldata.push({ r: 0, c: 0, v: HEADER(rowHeader) });
    if (includeNominal) celldata.push({ r: 0, c: nomCol, v: HEADER('Nominal value (mm)') });
    for (let i = 0; i < colCount; i++) {
      celldata.push({ r: 0, c: slStart + i, v: HEADER(`${colPrefix}${i + 1}`) });
    }

    // One row per label — measurement (and nominal) cells left blank for user input
    rowLabels.forEach((label, idx) => {
      const r = idx + 1;
      celldata.push({ r, c: 0, v: HEADER(label) });
    });

    return [{
      name: 'UT Measurements',
      celldata,
      row: Math.max(50, rowLabels.length + 5),
      column: Math.max(26, slStart + colCount + 2),
    }];
  }

  // Apply a generated template by remounting the Workbook with new data.
  // FortuneSheet only reads its `data` prop on initial mount, and its
  // updateSheet API requires a 2D matrix + sheet id (not celldata), so
  // the cleanest cross-version fix is to bump a key and let React
  // remount the component fresh.
  function applyTemplate(newSheets) {
    baselineSheetsRef.current = newSheets;
    latestSheetsRef.current = newSheets;
    setSheets(newSheets);
    setWorkbookEpoch((n) => n + 1);
    // FortuneSheet doesn't auto-evaluate formulas loaded via celldata —
    // formula text is stored but the dependency graph that recomputes
    // on referenced-cell change is only built once each formula has
    // been seen by setCellValue → updateCell → execfunction. Without
    // this, summary cells (MIN/MAX/AVG/degradation) appear blank until
    // the user re-types each formula. Register every formula cell
    // ~200ms after the remount.
    registerFormulasInSheets(newSheets, 200);
  }

  // Walk every formula cell in `sheetsArg` and call setCellValue with
  // the same formula. That triggers FortuneSheet's updateCell path
  // which computes v + m and registers the cell in the dependency
  // graph so it auto-recomputes when referenced cells change.
  // The work runs inside the isApplyingFix guard so the move-detector
  // doesn't misinterpret these as user edits.
  function registerFormulasInSheets(sheetsArg, delayMs = 200) {
    setTimeout(() => {
      const wb = workbookRef.current;
      if (!wb || typeof wb.setCellValue !== 'function') return;
      if (!Array.isArray(sheetsArg)) return;
      isApplyingFixRef.current = true;
      try {
        for (const sheet of sheetsArg) {
          if (!sheet || !Array.isArray(sheet.celldata)) continue;
          for (const cd of sheet.celldata) {
            const fval = cd && cd.v && cd.v.f;
            if (typeof fval === 'string' && fval.startsWith('=')) {
              try { wb.setCellValue(cd.r, cd.c, { f: fval }); } catch { /* per-cell best-effort */ }
            }
          }
        }
      } finally {
        // Release the move-detector guard once FortuneSheet's onChange
        // events from the batch have all fired through.
        setTimeout(() => { isApplyingFixRef.current = false; }, 250);
      }
    }, delayMs);
  }

  // ── Auto-generate sub-card reference images on Configure-Layout Apply ──
  //
  // Calls the backend regen endpoint, then persists the resulting image IDs
  // on this sub-card's record via saveUTData (without overwriting the user's
  // in-flight sheet edits — we only touch image-related fields).
  async function triggerRecipeRegen(cfg) {
    if (!tableId) return;
    const layout = cfg?.recipe || {};
    let resp;
    try {
      resp = await regenerateSubcardImages(inspectionId, tableId, {
        component, measurement, layout,
      });
    } catch (err) {
      setImageWarning({
        subcardName: tableHeading,
        component, measurement, layout,
        haltReason: 'network_error',
        haltDetail: err?.message || 'Failed to reach the image generator.',
        failedOutputs: [],
      });
      return;
    }

    if (resp?.skipped) return; // no recipe → silent skip
    if (resp?.halt_reason) {
      setImageWarning({
        subcardName: tableHeading,
        component, measurement, layout,
        haltReason: resp.halt_reason,
        haltDetail: resp.halt_detail || '',
        failedOutputs: [],
      });
      return;
    }

    // Backend has already written subcardImageIds + shellCoursePer +
    // cleared generatedImagesStaleAt under the same db transaction as the
    // image inserts. Nothing more to persist here — just sync local state
    // and ping the opener so InspectionDetail refetches ut_data.
    const createdIds = resp.created_ids || [];
    setAllTables((prev) => prev.map((t) => {
      if (t.id !== tableId) return t;
      return {
        ...t,
        subcardImageIds: createdIds,
        shellCoursePer: layout.shellCoursePer ?? t.shellCoursePer ?? null,
        generatedImagesStaleAt: null,
      };
    }));
    try {
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage({
          type: 'tib:ut-saved', inspectionId, tableId,
        }, window.location.origin);
      }
    } catch {}

    // Surface per-output failures (succeeded outputs are still attached).
    if (Array.isArray(resp.failed_outputs) && resp.failed_outputs.length > 0) {
      setImageWarning({
        subcardName: tableHeading,
        component, measurement, layout,
        haltReason: null,
        haltDetail: '',
        failedOutputs: resp.failed_outputs,
      });
    }
  }

  // ── Per-measurement custom buttons ─────────────────────────────
  // "Configure layout" appears for every component-measurement that has
  // a known layout template. The button is hidden for unknown combos.
  const customButtons = useMemo(() => {
    if (!getLayoutConfig(component, measurement)) return [];
    return [{ label: 'Configure layout', onClick: () => setShowLayoutCfg(true) }];
  }, [component, measurement]);

  // ── FortuneSheet hooks: lock down sheet-tab operations + force Arial ─
  const sheetHooks = useMemo(() => ({
    beforeAddSheet: () => false,            // No new worksheets
    beforeDeleteSheet: () => false,         // No deleting the only sheet
    beforeUpdateSheetName: () => false,     // No double-click rename
    // After every cell edit, stamp the cell's font to Arial unless the
    // user explicitly chose another font (which they can't, since the
    // font picker is hidden). The guard on `ff !== undefined && !== 0`
    // prevents recursion when setCellFormat triggers another update.
    afterUpdateCell: (row, col, oldVal, newVal) => {
      if (!workbookRef.current) return;
      if (!newVal || typeof newVal !== 'object') return;
      const ff = newVal.ff;
      if (ff !== undefined && ff !== null && ff !== 0 && ff !== '0') return;
      try {
        workbookRef.current.setCellFormat(row, col, 'ff', ARIAL_FF);
      } catch { /* swallow — font stamping is best-effort */ }
    },
  }), []);

  // Keep latestSheetsRef in lock-step with state so the (memoised)
  // hook closures below always read the freshest sheets array.
  latestSheetsRef.current = sheets;

  if (!sheets) {
    return <div className="ut-loading">Loading…</div>;
  }

  return (
    <div className="ut-page" ref={containerRef}>
      <div className="ut-toolbar">
        <span className="ut-toolbar-title">{tableHeading}</span>
        <div className="ut-toolbar-actions">
          {customButtons.map((btn) => (
            <button
              key={btn.label}
              className="ut-btn ut-btn-secondary"
              onClick={btn.onClick}
            >
              {btn.label}
            </button>
          ))}
          <button
            className="ut-btn ut-btn-secondary"
            onClick={() => setShowFunctions(true)}
          >
            Functions
          </button>
          <button
            className="ut-btn ut-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
          {saveMsg && <span className="ut-save-msg">{saveMsg}</span>}
          <button
            className="ut-btn ut-btn-close"
            onClick={() => window.close()}
          >
            Close
          </button>
        </div>
      </div>

      <div className="ut-sheet-container">
        <Workbook
          key={workbookEpoch}
          ref={workbookRef}
          data={sheets}
          onChange={handleSheetsChange}
          toolbarItems={ALLOWED_TOOLBAR_ITEMS}
          cellContextMenu={ALLOWED_CELL_CONTEXT}
          sheetTabContextMenu={EMPTY_SHEET_TAB_CONTEXT}
          showSheetTabs={false}
          hooks={sheetHooks}
        />
      </div>

      {showFunctions && (
        <UTFunctionsModal
          workbookRef={workbookRef}
          onCancel={() => setShowFunctions(false)}
        />
      )}

      {showLayoutCfg && (
        <UTLayoutConfigModal
          component={component}
          measurement={measurement}
          scanlineCount={scanlineCount}
          positionCount={positionCount}
          onCancel={() => setShowLayoutCfg(false)}
          onConfirm={(cfg) => {
            const newSheets = cfg.specialType === 'nozzle-locations'
              ? buildNozzleTemplate(cfg)
              : cfg.specialType === 'rafter-locations'
              ? buildRafterTemplate(cfg)
              : cfg.specialType === 'supporting-column'
              ? buildPipeSegmentTemplate({ ...cfg, pipes: cfg.columns, groupHeader: 'Column' })
              : cfg.specialType === 'shell-course'
              ? buildShellCourseTemplate(cfg)
              : cfg.specialType === 'pipe-segment'
              ? buildPipeSegmentTemplate(cfg)
              : cfg.specialType === 'outside-edge-bottom'
              ? buildOutsideEdgeBottomTemplate(cfg)
              : cfg.specialType === 'annular-plates'
              ? buildAnnularPlatesTemplate(cfg)
              : cfg.specialType === 'flat-bottom'
              ? buildFlatBottomTemplate(cfg)
              : cfg.specialType === 'corner-weld'
              ? buildCornerWeldTemplate(cfg)
              : buildLayoutTemplate(cfg);
            applyTemplate(newSheets);
            setShowLayoutCfg(false);
            // Fire-and-forget regen — the user typically has 30+ seconds of
            // data entry ahead before Save, so the generated images are
            // ready well in time. We persist them on the sub-card record
            // immediately so refreshes/closes don't lose the linkage.
            triggerRecipeRegen(cfg).catch((err) => {
              console.error('Recipe regen failed:', err);
            });
          }}
        />
      )}

      {imageWarning && (
        <SubcardImageWarningModal
          {...imageWarning}
          onClose={() => setImageWarning(null)}
        />
      )}
    </div>
  );
}
